import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
// v2.3.3 — Leaflet pour mini-preview dans modales (dep déjà installée v2.0.0).
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
// v2.11.0 — Onglet CRUD events Live.
import OpsLiveEventsTab from "@/components/OpsLiveEventsTab";
// v2.12.0 — Onglet modération profils Santé + Justice (sponsored_tier + verified + soft-delete).
import OpsProfilesTab from "@/components/OpsProfilesTab";
// v5.6.0 — Onglet administration des données mock AYA Search.
import OpsMockDataTab from "@/components/OpsMockDataTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "jsonfirst_ops_token";

function authHeaders(token) {
  return { "X-Admin-Token": token };
}

export default function OpsDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [tab, setTab] = useState("modes");

  useEffect(() => {
    if (token) verifyToken(token);
  }, []); // eslint-disable-line

  const verifyToken = async (t) => {
    try {
      const r = await axios.post(`${API}/ops/auth`, { token: t });
      if (r.data?.ok) {
        setAuthed(true);
        sessionStorage.setItem(SESSION_KEY, t);
      } else {
        setAuthed(false);
        setAuthErr("Token invalide");
      }
    } catch (e) {
      setAuthed(false);
      setAuthErr(e.response?.status === 503 ? "ADMIN_TOKEN non configuré côté serveur" : "Token invalide");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (token.trim()) verifyToken(token.trim());
  };
  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setToken("");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="ops-root ops-auth-gate">
        <form className="ops-auth-box" onSubmit={handleLogin} data-testid="ops-auth-form">
          <div className="ops-auth-logo">⚙️</div>
          <h1>AYA Operations</h1>
          <p className="ops-auth-hint">Admin Token requis · AYA Internal</p>
          <input
            type="password"
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="ops-auth-input"
            placeholder="ADMIN_TOKEN"
            data-testid="ops-token-input"
          />
          {authErr && <div className="ops-auth-err">{authErr}</div>}
          <button type="submit" className="ops-btn ops-btn-primary" data-testid="ops-auth-submit">
            Entrer
          </button>
          <a href="/" className="ops-auth-back">← Retour au chat</a>
        </form>
      </div>
    );
  }

  return (
    <div className="ops-root">
      <header className="ops-header">
        <div className="ops-header-title">
          <span>⚙️</span>
          <h1>AYA Operations <span style={{ color: "#2EE27B", fontSize: 12, fontWeight: 800 }}>· OPS Principal</span></h1>
        </div>
        <div className="ops-header-actions">
          <a href="/internal/operations" className="ops-btn ops-btn-ghost" data-testid="ops-to-internal" style={{ borderColor: "rgba(46,226,123,0.4)", color: "#2EE27B" }}>⌘ AYA Internal</a>
          <a href="/" className="ops-btn ops-btn-ghost">← Retour au chat</a>
          <button className="ops-btn ops-btn-ghost" onClick={handleLogout} data-testid="ops-logout">
            Logout
          </button>
        </div>
      </header>

      <nav className="ops-tabs">
        {[
          ["modes", "🎭 Modes"],
          ["implants", "💉 Implants"],
          ["llm", "🧠 Santé LLM"],
          ["web_search", "🌐 Web Search"],
          ["sponsored", "🎯 Sponsored"],
          ["sponsored_recs", "🤝 Recos"],
          ["voice_languages", "🌍 Langues"],
          ["rag", "🧠 RAG"],
          ["intents", "🎯 Intents RAG"],
          ["personalities", "👤 Personnalités"],
          ["commerces", "🏪 Commerces"],
          ["addresses", "📍 Adresses"],
          ["payments", "💳 Paiements"],
          ["mock_data", "🧪 Données Test"],
          ["live_events", "🎫 Events Live"],
          ["health_profiles", "👨‍⚕️ Santé"],
          ["justice_profiles", "⚖️ Justice"],
          ["newspapers", "🤝 Partenaires"],
          ["lexicons", "🌍 Lexiques"],
          ["keys", "🔑 Clés API"],
          ["manifest", "🛡️ Manifest"],
          ["purge", "🧹 Purge"],
          ["audit", "📜 Audit Log"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`ops-tab ${tab === k ? "is-active" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`ops-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </nav>

      <main className="ops-main">
        {tab === "modes" && <ModesTab token={token} />}
        {tab === "implants" && <ImplantsTab token={token} />}
        {tab === "llm" && <LLMHealthTab token={token} />}
        {tab === "web_search" && <WebSearchTab token={token} />}
        {tab === "sponsored" && <SponsoredTab token={token} />}
        {tab === "sponsored_recs" && <SponsoredRecsTab token={token} />}
        {tab === "voice_languages" && <VoiceLanguagesTab token={token} />}
        {tab === "rag" && <RagTab token={token} />}
        {tab === "intents" && <IntentsTab token={token} />}
        {tab === "personalities" && <PersonalitiesTab token={token} />}
        {tab === "commerces" && <CommercesTab token={token} />}
        {tab === "addresses" && <AddressesTab token={token} />}
        {tab === "payments" && <PaymentsTab token={token} />}
        {tab === "mock_data" && <OpsMockDataTab token={token} />}
        {tab === "live_events" && <OpsLiveEventsTab token={token} />}
        {tab === "health_profiles" && <OpsProfilesTab token={token} kind="health" />}
        {tab === "justice_profiles" && <OpsProfilesTab token={token} kind="justice" />}
        {tab === "newspapers" && <NewspapersTab token={token} />}
        {tab === "lexicons" && <LexiconsTab token={token} />}
        {tab === "keys" && <KeysTab token={token} />}
        {tab === "manifest" && <ManifestTab token={token} />}
        {tab === "purge" && <PurgeTab token={token} />}
        {tab === "audit" && <AuditTab token={token} />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MODES TAB
// ---------------------------------------------------------------------------

// Voice block sub-fieldset: TTS pre-normalization controls + live tester.
const ALL_TTS_RULES = [
  { id: "strip_markdown",         label: "Strip markdown (** ## [1] —)" },
  { id: "expand_phone_numbers",   label: "Phone numbers (+225 07 12 → plus deux cent vingt-cinq, zéro sept, douze)" },
  { id: "expand_currency",        label: "Currency (FCFA → francs CFA)" },
  { id: "expand_dates",           label: "Dates (05/12/2026 → cinq décembre)" },
  { id: "expand_numbers",         label: "Numbers (5000 → cinq mille, 20% → vingt pour cent)" },
  { id: "expand_abbreviations",   label: "Abbreviations (ex. → par exemple, cf.)" },
  { id: "expand_acronyms",        label: "Acronyms (RTI → R T I)" },
  { id: "normalize_punctuation",  label: "Punctuation (: → ,  ;  → .  …)" },
];

function TtsNormalizationFieldset({ voiceCfg = {}, onToggle, onRulesChange, token }) {
  const enabled = voiceCfg.pre_tts_normalization !== false;
  const rules = Array.isArray(voiceCfg.normalization_rules) && voiceCfg.normalization_rules.length
    ? voiceCfg.normalization_rules
    : ALL_TTS_RULES.map((r) => r.id);
  const [testInput, setTestInput] = useState("Le prix est de 5000 FCFA, soit environ 20% de moins. Voir RTI ex. ou cf. abidjan.net.");
  const [testOut, setTestOut] = useState(null);
  const [testing, setTesting] = useState(false);

  const toggleRule = (id) => {
    const next = rules.includes(id) ? rules.filter((r) => r !== id) : [...rules, id];
    onRulesChange(next);
  };
  const runTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(
        `${API}/ops/test/tts-normalization`,
        { text: testInput, rules },
        { headers: authHeaders(token) }
      );
      setTestOut(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <fieldset className="ops-fieldset" data-testid="ops-tts-norm-fieldset">
      <legend>🔊 TTS pre-normalization</legend>
      <p className="ops-warn" style={{ marginTop: 0 }}>
        Pre-process le texte LLM <strong>avant</strong> ElevenLabs pour éviter "F C F A" épelé, "5000" lu en chiffres bizarres, et le markdown lu littéralement.
      </p>
      <label className="ops-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid="ops-tts-norm-enabled"
        />
        <span>Pre-TTS normalization (default ON)</span>
      </label>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, opacity: enabled ? 1 : 0.4 }}>Règles actives :</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, opacity: enabled ? 1 : 0.4 }}>
          {ALL_TTS_RULES.map((r) => (
            <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={rules.includes(r.id)}
                onChange={() => toggleRule(r.id)}
                disabled={!enabled}
                data-testid={`ops-tts-rule-${r.id}`}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 14, borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>🧪 Tester avec les règles cochées :</div>
        <textarea
          rows={2}
          value={testInput}
          onChange={(e) => setTestInput(e.target.value)}
          className="ops-textarea"
          data-testid="ops-tts-test-input"
        />
        <button
          type="button"
          className="ops-btn ops-btn-primary"
          onClick={runTest}
          disabled={testing || !testInput.trim()}
          data-testid="ops-tts-test-run"
          style={{ marginTop: 6 }}
        >{testing ? "…" : "Tester normalisation"}</button>
        {testOut && (
          <div style={{ marginTop: 10, fontSize: 12, background: "#0b1220", color: "#e2e8f0", padding: 10, borderRadius: 6 }} data-testid="ops-tts-test-output">
            <div style={{ opacity: 0.7, marginBottom: 4 }}>Original →</div>
            <div style={{ marginBottom: 6 }}>{testOut.original}</div>
            <div style={{ opacity: 0.7, marginBottom: 4 }}>Normalisé →</div>
            <div style={{ color: "#86efac", whiteSpace: "pre-wrap" }}>{testOut.normalized}</div>
            <div style={{ opacity: 0.5, fontSize: 10, marginTop: 6 }}>
              Règles appliquées : {(testOut.applied_rules || []).join(", ")}
            </div>
          </div>
        )}
      </div>
    </fieldset>
  );
}

function ModesTab({ token }) {
  const [modes, setModes] = useState(null);
  const [activeMode, setActiveMode] = useState("casual");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState("");
  const [backupsOpen, setBackupsOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [allImplants, setAllImplants] = useState([]);

  const load = async () => {
    const r = await axios.get(`${API}/ops/modes`, { headers: authHeaders(token) });
    setModes(r.data.modes);
    try {
      const ri = await axios.get(`${API}/ops/implants`, { headers: authHeaders(token) });
      setAllImplants(ri.data.implants || []);
    } catch (_) {}
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (modes && modes[activeMode]) {
      setDraft(JSON.parse(JSON.stringify(modes[activeMode])));
      setRawText(JSON.stringify(modes[activeMode], null, 2));
    }
  }, [modes, activeMode]);

  if (!modes || !draft) return <div className="ops-loading">Chargement…</div>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(modes[activeMode]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = rawMode ? JSON.parse(rawText) : draft;
      const r = await axios.put(`${API}/ops/modes/${activeMode}`, payload, { headers: authHeaders(token) });
      toast.success(`Mode ${activeMode} sauvegardé`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setDraft(JSON.parse(JSON.stringify(modes[activeMode])));
    setRawText(JSON.stringify(modes[activeMode], null, 2));
    toast("Formulaire réinitialisé");
  };

  const openBackups = async () => {
    const r = await axios.get(`${API}/ops/modes/${activeMode}/backups`, { headers: authHeaders(token) });
    setBackups(r.data.backups || []);
    setBackupsOpen(true);
  };

  const rollback = async (filename) => {
    if (!window.confirm(`Restaurer ${filename} ?`)) return;
    try {
      await axios.post(`${API}/ops/modes/${activeMode}/rollback`, { backup_filename: filename }, { headers: authHeaders(token) });
      toast.success("Rollback effectué");
      setBackupsOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const testVoice = async () => {
    try {
      const r = await axios.post(
        `${API}/ops/test/elevenlabs`,
        { voice_id: draft.voice.voice_id, mode: activeMode },
        { headers: authHeaders(token) }
      );
      if (r.data.ok) {
        toast.success(`🔊 ElevenLabs OK — ${r.data.audio_bytes} bytes, ${r.data.latency_ms}ms`);
      } else {
        toast.error(`Erreur voix : ${r.data.error || r.data.body}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.body || e.message);
    }
  };

  const testLLM = async () => {
    try {
      const r = await axios.post(
        `${API}/ops/test/llm`,
        { provider: draft.llm.provider, model: draft.llm.model, mode: activeMode },
        { headers: authHeaders(token) }
      );
      if (r.data.ok) {
        toast.success(`🧠 ${r.data.provider}/${r.data.model} OK — ${r.data.latency_ms}ms — "${r.data.sample}"`);
      } else {
        toast.error(`LLM erreur : ${r.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const update = (path, value) => {
    const parts = path.split(".");
    const next = JSON.parse(JSON.stringify(draft));
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
    cur[parts[parts.length - 1]] = value;
    setDraft(next);
  };

  return (
    <div className="ops-modes">
      <div className="ops-modes-submenu">
        {["casual", "street", "business"].map((m) => (
          <button
            key={m}
            type="button"
            className={`ops-submenu-btn ${activeMode === m ? "is-active" : ""}`}
            onClick={() => setActiveMode(m)}
            data-testid={`ops-mode-tab-${m}`}
          >
            <span style={{ color: modes[m].color }}>●</span> {modes[m].label}
          </button>
        ))}
        <div className="ops-submenu-spacer" />
        <label className="ops-raw-toggle">
          <input type="checkbox" checked={rawMode} onChange={(e) => setRawMode(e.target.checked)} />
          Raw JSON
        </label>
      </div>

      {rawMode ? (
        <div className="ops-form">
          <label className="ops-field">
            <span>JSON brut</span>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                try { setDraft(JSON.parse(e.target.value)); } catch (_) {}
              }}
              className="ops-textarea ops-mono"
              rows={30}
              data-testid="ops-raw-json"
            />
          </label>
        </div>
      ) : (
        <div className="ops-form">
          <div className="ops-form-row">
            <label className="ops-field">
              <span>Label</span>
              <input type="text" value={draft.label} onChange={(e) => update("label", e.target.value)} className="ops-input" />
            </label>
            <label className="ops-field ops-field-sm">
              <span>Couleur</span>
              <input type="color" value={draft.color} onChange={(e) => update("color", e.target.value)} className="ops-color" />
            </label>
          </div>

          <label className="ops-field">
            <span>System prompt</span>
            <textarea
              value={draft.system_prompt}
              onChange={(e) => update("system_prompt", e.target.value)}
              rows={7}
              className="ops-textarea ops-mono"
              data-testid="ops-system-prompt"
            />
            <small>{draft.system_prompt.length} / 8000 chars</small>
          </label>

          <label className="ops-field">
            <span>Temperature ({draft.temperature.toFixed(2)}, cap manifest = 0.7)</span>
            <input
              type="range" min="0" max="1" step="0.05"
              value={draft.temperature}
              onChange={(e) => update("temperature", parseFloat(e.target.value))}
            />
          </label>

          <fieldset className="ops-fieldset">
            <legend>🧠 LLM (chain)</legend>
            <div className="ops-form-row">
              <label className="ops-field">
                <span>Primary provider</span>
                <select value={draft.llm.provider} onChange={(e) => update("llm.provider", e.target.value)} className="ops-input">
                  <option value="mistral">mistral</option>
                  <option value="openai">openai</option>
                  <option value="gemini">gemini</option>
                  <option value="anthropic">anthropic</option>
                </select>
              </label>
              <label className="ops-field">
                <span>Primary model</span>
                <input type="text" value={draft.llm.model} onChange={(e) => update("llm.model", e.target.value)} className="ops-input" />
              </label>
              <label className="ops-field ops-field-sm">
                <span>Via</span>
                <select value={draft.llm.via || "emergent"} onChange={(e) => update("llm.via", e.target.value)} className="ops-input">
                  <option value="emergent">emergent</option>
                  <option value="direct_or_emergent">direct_or_emergent</option>
                </select>
              </label>
              <button type="button" className="ops-btn ops-btn-ghost" onClick={testLLM} data-testid="ops-test-llm">
                Test 🧠
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong style={{ fontSize: 13 }}>↘ Fallback chain (ordre = priorité)</strong>
              {(draft.llm.fallback_chain || []).length === 0 && (
                <p style={{ opacity: 0.6, marginTop: 6 }}>Aucun fallback configuré.</p>
              )}
              {(draft.llm.fallback_chain || []).map((fb, idx) => (
                <div key={idx} className="ops-form-row" style={{ alignItems: "center", marginTop: 6 }} data-testid={`ops-fb-${idx}`}>
                  <span className="ops-mono" style={{ width: 24, textAlign: "center", opacity: 0.6 }}>{idx + 1}.</span>
                  <select
                    value={fb.provider}
                    onChange={(e) => {
                      const next = [...draft.llm.fallback_chain];
                      next[idx] = { ...next[idx], provider: e.target.value };
                      update("llm.fallback_chain", next);
                    }}
                    className="ops-input"
                  >
                    <option value="mistral">mistral</option>
                    <option value="openai">openai</option>
                    <option value="gemini">gemini</option>
                    <option value="anthropic">anthropic</option>
                  </select>
                  <input
                    type="text"
                    value={fb.model}
                    onChange={(e) => {
                      const next = [...draft.llm.fallback_chain];
                      next[idx] = { ...next[idx], model: e.target.value };
                      update("llm.fallback_chain", next);
                    }}
                    className="ops-input ops-mono"
                    style={{ flex: 2 }}
                  />
                  <select
                    value={fb.via || "emergent"}
                    onChange={(e) => {
                      const next = [...draft.llm.fallback_chain];
                      next[idx] = { ...next[idx], via: e.target.value };
                      update("llm.fallback_chain", next);
                    }}
                    className="ops-input"
                  >
                    <option value="emergent">emergent</option>
                    <option value="direct_or_emergent">direct_or_emergent</option>
                  </select>
                  <button
                    type="button" className="ops-btn ops-btn-sm" title="Promouvoir en primary"
                    data-testid={`ops-fb-promote-${idx}`}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/ops/llm/swap`, {
                          mode: activeMode,
                          new_primary: { provider: fb.provider, model: fb.model, via: fb.via || "emergent" },
                          demote_to: "fallback_top",
                        }, { headers: authHeaders(token) });
                        toast.success("Promu en primary");
                        await load();
                      } catch (e) { toast.error(e.response?.data?.error || e.message); }
                    }}
                  >↑</button>
                  <button
                    type="button" className="ops-btn ops-btn-sm"
                    onClick={() => {
                      const next = [...draft.llm.fallback_chain];
                      if (idx > 0) { [next[idx-1], next[idx]] = [next[idx], next[idx-1]]; update("llm.fallback_chain", next); }
                    }}
                  >▲</button>
                  <button
                    type="button" className="ops-btn ops-btn-sm"
                    onClick={() => {
                      const next = [...draft.llm.fallback_chain];
                      if (idx < next.length - 1) { [next[idx], next[idx+1]] = [next[idx+1], next[idx]]; update("llm.fallback_chain", next); }
                    }}
                  >▼</button>
                  <button
                    type="button" className="ops-btn ops-btn-sm"
                    onClick={() => {
                      const next = (draft.llm.fallback_chain || []).filter((_, i) => i !== idx);
                      update("llm.fallback_chain", next);
                    }}
                  >🗑️</button>
                </div>
              ))}
              <button
                type="button" className="ops-btn ops-btn-ghost" style={{ marginTop: 8 }}
                onClick={() => {
                  const next = [...(draft.llm.fallback_chain || []), { provider: "openai", model: "gpt-4o-mini", via: "emergent" }];
                  update("llm.fallback_chain", next);
                }}
                data-testid="ops-fb-add"
              >+ Ajouter un fallback</button>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong style={{ fontSize: 13 }}>⚙️ Triggers</strong>
              <div className="ops-form-row" style={{ flexWrap: "wrap", gap: 12, marginTop: 6 }}>
                {[
                  ["on_http_5xx", "5xx"],
                  ["on_http_429", "429"],
                  ["on_invalid_key", "401/403"],
                  ["on_empty_output", "empty output"],
                  ["on_network_error", "network"],
                ].map(([k, lbl]) => (
                  <label key={k} className="ops-field-inline">
                    <input
                      type="checkbox"
                      checked={!!(draft.llm.fallback_triggers && draft.llm.fallback_triggers[k])}
                      onChange={(e) => update(`llm.fallback_triggers.${k}`, e.target.checked)}
                    />
                    <span style={{ marginLeft: 6 }}>{lbl}</span>
                  </label>
                ))}
                <label className="ops-field ops-field-sm">
                  <span>timeout_ms</span>
                  <input
                    type="number" min="0" max="60000" step="500"
                    value={draft.llm.fallback_triggers?.on_timeout_ms ?? 15000}
                    onChange={(e) => update("llm.fallback_triggers.on_timeout_ms", parseInt(e.target.value, 10) || 0)}
                    className="ops-input"
                  />
                </label>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <strong style={{ fontSize: 13 }}>🔌 Circuit breaker</strong>
              <div className="ops-form-row" style={{ marginTop: 6 }}>
                <label className="ops-field-inline">
                  <input
                    type="checkbox"
                    checked={draft.llm.circuit_breaker?.enabled !== false}
                    onChange={(e) => update("llm.circuit_breaker.enabled", e.target.checked)}
                  />
                  <span style={{ marginLeft: 6 }}>Activé</span>
                </label>
                <label className="ops-field ops-field-sm">
                  <span>Failure threshold</span>
                  <input
                    type="number" min="1" max="20"
                    value={draft.llm.circuit_breaker?.failure_threshold ?? 3}
                    onChange={(e) => update("llm.circuit_breaker.failure_threshold", parseInt(e.target.value, 10) || 3)}
                    className="ops-input"
                  />
                </label>
                <label className="ops-field ops-field-sm">
                  <span>Cooldown (ms)</span>
                  <input
                    type="number" min="1000" max="600000" step="1000"
                    value={draft.llm.circuit_breaker?.cooldown_ms ?? 120000}
                    onChange={(e) => update("llm.circuit_breaker.cooldown_ms", parseInt(e.target.value, 10) || 120000)}
                    className="ops-input"
                  />
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset className="ops-fieldset">
            <legend>🔊 Voix</legend>
            <div className="ops-form-row">
              <label className="ops-field-inline">
                <input type="checkbox" checked={draft.voice.enabled} onChange={(e) => update("voice.enabled", e.target.checked)} />
                Activée
              </label>
              <label className="ops-field">
                <span>voice_id</span>
                <input type="text" value={draft.voice.voice_id} onChange={(e) => update("voice.voice_id", e.target.value)} className="ops-input" />
              </label>
              <label className="ops-field">
                <span>model_id (latence ↓ → ↑ qualité)</span>
                <select
                  value={draft.voice.model_id || "eleven_flash_v2_5"}
                  onChange={(e) => update("voice.model_id", e.target.value)}
                  className="ops-input"
                  data-testid="ops-voice-model-id"
                >
                  <option value="eleven_flash_v2_5">eleven_flash_v2_5 (~75ms, recommandé)</option>
                  <option value="eleven_turbo_v2_5">eleven_turbo_v2_5 (~150ms)</option>
                  <option value="eleven_multilingual_v2">eleven_multilingual_v2 (~300ms, qualité max)</option>
                  <option value="eleven_multilingual_v1">eleven_multilingual_v1</option>
                </select>
              </label>
              <button type="button" className="ops-btn ops-btn-ghost" onClick={testVoice} data-testid="ops-test-voice">
                Test 🔊
              </button>
            </div>
            <div className="ops-form-row">
              <label className="ops-field">
                <span>Stability ({(draft.voice.stability ?? 0.5).toFixed(2)})</span>
                <input type="range" min="0" max="1" step="0.05" value={draft.voice.stability ?? 0.5} onChange={(e) => update("voice.stability", parseFloat(e.target.value))} />
              </label>
              <label className="ops-field">
                <span>Similarity boost ({(draft.voice.similarity_boost ?? 0.75).toFixed(2)})</span>
                <input type="range" min="0" max="1" step="0.05" value={draft.voice.similarity_boost ?? 0.75} onChange={(e) => update("voice.similarity_boost", parseFloat(e.target.value))} />
              </label>
            </div>
          </fieldset>

          <TtsNormalizationFieldset
            voiceCfg={draft.voice}
            onToggle={(v) => update("voice.pre_tts_normalization", v)}
            onRulesChange={(arr) => update("voice.normalization_rules", arr)}
            token={token}
          />

          <fieldset className="ops-fieldset">
            <legend>🎨 Persona wrap</legend>
            <div className="ops-form-row">
              <label className="ops-field">
                <span>Prefix</span>
                <textarea value={draft.persona_wrap.prefix} onChange={(e) => update("persona_wrap.prefix", e.target.value)} rows={2} className="ops-textarea" />
              </label>
              <label className="ops-field">
                <span>Suffix</span>
                <textarea value={draft.persona_wrap.suffix} onChange={(e) => update("persona_wrap.suffix", e.target.value)} rows={2} className="ops-textarea" />
              </label>
            </div>
          </fieldset>

          <fieldset className="ops-fieldset">
            <legend>🛡️ Guards</legend>
            <div className="ops-form-row">
              <label className="ops-field ops-field-sm">
                <span>Max history</span>
                <input type="number" min="0" max="50" value={draft.guards.max_history} onChange={(e) => update("guards.max_history", parseInt(e.target.value, 10))} className="ops-input" />
              </label>
              <label className="ops-field ops-field-sm">
                <span>Max message length</span>
                <input type="number" min="1" max="8000" value={draft.guards.max_message_length} onChange={(e) => update("guards.max_message_length", parseInt(e.target.value, 10))} className="ops-input" />
              </label>
            </div>
          </fieldset>

          <fieldset className="ops-fieldset">
            <legend>💉 Implants actifs</legend>
            <p className="ops-warn" style={{ marginTop: 0 }}>
              Active/désactive les implants compatibles avec le mode <code>{activeMode}</code>. Les implants shaped le prompt LLM côté serveur.
            </p>
            {allImplants.filter((i) => (i.compatible_modes || []).includes(activeMode)).length === 0 && (
              <p>Aucun implant compatible avec ce mode.</p>
            )}
            {allImplants
              .filter((i) => (i.compatible_modes || []).includes(activeMode))
              .map((imp) => {
                const active = Array.isArray(draft.implants) && draft.implants.includes(imp.id);
                return (
                  <label key={imp.id} className="ops-field-inline" data-testid={`ops-mode-implant-${imp.id}`}>
                    <input
                      type="checkbox"
                      checked={active}
                      disabled={!imp.enabled}
                      onChange={(e) => {
                        const cur = Array.isArray(draft.implants) ? [...draft.implants] : [];
                        const next = e.target.checked
                          ? (cur.includes(imp.id) ? cur : [...cur, imp.id])
                          : cur.filter((x) => x !== imp.id);
                        update("implants", next);
                      }}
                    />
                    <span style={{ marginLeft: 8 }}>
                      <strong>{imp.id}</strong> <span className="ops-mono">v{imp.version}</span>
                      {!imp.enabled && <em style={{ color: "#dc3545", marginLeft: 8 }}>(désactivé globalement)</em>}
                      {imp.strict_enforce && <em style={{ color: "#d97706", marginLeft: 8 }}>strict</em>}
                    </span>
                  </label>
                );
              })}
          </fieldset>
        </div>
      )}

      <div className="ops-actions">
        <button
          type="button"
          className="ops-btn ops-btn-primary"
          onClick={save}
          disabled={saving || (!dirty && !rawMode)}
          data-testid="ops-save-mode"
        >
          💾 {saving ? "Sauvegarde…" : "Sauvegarder"}
        </button>
        <button type="button" className="ops-btn ops-btn-ghost" onClick={reset}>↺ Réinitialiser</button>
        <button type="button" className="ops-btn ops-btn-ghost" onClick={openBackups} data-testid="ops-backups-btn">
          📜 Historique backups
        </button>
      </div>

      {backupsOpen && (
        <div className="ops-modal-backdrop" onClick={() => setBackupsOpen(false)}>
          <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Backups — {activeMode}</h3>
            {backups.length === 0 && <p>Aucun backup</p>}
            <ul className="ops-backups-list">
              {backups.map((b) => (
                <li key={b.filename}>
                  <span className="ops-mono">{b.filename}</span>
                  <button className="ops-btn ops-btn-sm" onClick={() => rollback(b.filename)}>Restaurer</button>
                </li>
              ))}
            </ul>
            <button className="ops-btn ops-btn-ghost" onClick={() => setBackupsOpen(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KEYS TAB
// ---------------------------------------------------------------------------
const KEY_META = [
  { key: "elevenlabs_api_key", label: "ELEVENLABS_API_KEY", test: "elevenlabs" },
  { key: "emergent_llm_key", label: "EMERGENT_LLM_KEY", test: "llm", llmFor: "casual" },
  { key: "tavily_api_key", label: "TAVILY_API_KEY (Web Search)", test: "tavily" },
  { key: "huggingface_token", label: "HUGGINGFACE_TOKEN (NLLB / fallback)", test: "huggingface" },
  { key: "mistral_api_key", label: "MISTRAL_API_KEY", test: null },
  { key: "anthropic_api_key", label: "ANTHROPIC_API_KEY", test: null },
  { key: "gemini_api_key", label: "GEMINI_API_KEY", test: null },
];

function KeysTab({ token }) {
  const [snapshot, setSnapshot] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [revealed, setRevealed] = useState({});

  const load = async () => {
    const r = await axios.get(`${API}/ops/keys`, { headers: authHeaders(token) });
    setSnapshot(r.data.keys);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!snapshot) return <div className="ops-loading">Chargement…</div>;

  const save = async () => {
    const partial = {};
    for (const k of Object.keys(drafts)) if (drafts[k] != null && drafts[k] !== "") partial[k] = drafts[k];
    if (Object.keys(partial).length === 0) { toast("Aucune clé modifiée"); return; }
    try {
      await axios.put(`${API}/ops/keys`, partial, { headers: authHeaders(token) });
      toast.success(`Clés sauvegardées : ${Object.keys(partial).join(", ")}`);
      setDrafts({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const testKey = async (meta) => {
    try {
      if (meta.test === "elevenlabs") {
        const r = await axios.post(`${API}/ops/test/elevenlabs`, {}, { headers: authHeaders(token) });
        r.data.ok
          ? toast.success(`🔊 OK — ${r.data.audio_bytes} bytes, ${r.data.latency_ms}ms`)
          : toast.error(`Erreur : ${r.data.error || r.data.body}`);
      } else if (meta.test === "llm") {
        const r = await axios.post(`${API}/ops/test/llm`, { mode: meta.llmFor || "casual" }, { headers: authHeaders(token) });
        r.data.ok ? toast.success(`🧠 ${r.data.provider} OK — ${r.data.latency_ms}ms`) : toast.error(r.data.error);
      } else if (meta.test === "tavily") {
        const r = await axios.post(`${API}/ops/test/tavily`, { query: "test ping" }, { headers: authHeaders(token) });
        r.data.ok
          ? toast.success(`🌐 Tavily OK — ${r.data.sources_count} sources, ${r.data.latency_ms}ms`)
          : toast.error(`Tavily : ${r.data.error}`);
      } else if (meta.test === "huggingface") {
        const r = await axios.post(`${API}/ops/test/huggingface`, {}, { headers: authHeaders(token) });
        r.data.ok
          ? toast.success(`🌍 Translator OK — ${r.data.latency_ms}ms · "${(r.data.translated || '').slice(0,40)}"`)
          : toast.error(`Translator : ${r.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.body || e.message);
    }
  };

  return (
    <div className="ops-keys">
      <p className="ops-warn">
        ⚠️ Les clés sont stockées en clair dans <code>runtime_config.json</code>. Ce fichier doit rester local et hors versioning.
      </p>
      {KEY_META.map((meta) => {
        const snap = snapshot[meta.key] || {};
        const isRevealed = revealed[meta.key];
        const draftVal = drafts[meta.key];
        return (
          <div className="ops-key-row" key={meta.key} data-testid={`ops-key-${meta.key}`}>
            <label className="ops-field">
              <span>{meta.label}</span>
              <div className="ops-key-line">
                <input
                  type={isRevealed ? "text" : "password"}
                  value={draftVal != null ? draftVal : ""}
                  placeholder={snap.configured ? `configuré (${snap.preview}, src=${snap.source})` : "non configuré"}
                  onChange={(e) => setDrafts({ ...drafts, [meta.key]: e.target.value })}
                  className="ops-input ops-mono"
                />
                <button
                  type="button"
                  className="ops-btn ops-btn-sm"
                  onClick={() => {
                    setRevealed({ ...revealed, [meta.key]: true });
                    setTimeout(() => setRevealed((r) => ({ ...r, [meta.key]: false })), 3000);
                  }}
                >
                  👁 3s
                </button>
                {meta.test && (
                  <button type="button" className="ops-btn ops-btn-sm" onClick={() => testKey(meta)}>
                    Test
                  </button>
                )}
              </div>
            </label>
          </div>
        );
      })}
      <div className="ops-actions">
        <button type="button" className="ops-btn ops-btn-primary" onClick={save} data-testid="ops-save-keys">
          💾 Sauvegarder les clés modifiées
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MANIFEST TAB
// ---------------------------------------------------------------------------
function ManifestTab({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ops/manifest`, { headers: authHeaders(token) }).then((r) => setData(r.data));
  }, [token]);
  if (!data) return <div className="ops-loading">Chargement…</div>;
  return (
    <div className="ops-manifest">
      <div className="ops-manifest-meta">
        <span><strong>Fichier :</strong> <code>{data.file}</code></span>
        <span><strong>Modifié :</strong> {data.last_modified}</span>
        <span><strong>Taille :</strong> {data.size_bytes} bytes</span>
      </div>
      <pre className="ops-pre ops-mono">{JSON.stringify(data.manifest, null, 2)}</pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AUDIT TAB
// ---------------------------------------------------------------------------
function AuditTab({ token }) {
  const [events, setEvents] = useState([]);
  const [decisionFilter, setDecisionFilter] = useState("ALL");
  const [ruleFilter, setRuleFilter] = useState("");
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ops/audit`, { headers: authHeaders(token) });
      setEvents(r.data.events || []);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 10000);
    return () => clearInterval(timerRef.current);
  }, [token]); // eslint-disable-line

  const filtered = events
    .filter((e) => decisionFilter === "ALL" || e.decision === decisionFilter)
    .filter((e) => !ruleFilter || (e.rule_id || "").includes(ruleFilter))
    .slice()
    .reverse();

  return (
    <div className="ops-audit">
      <div className="ops-audit-controls">
        <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)} className="ops-input">
          <option value="ALL">Toutes décisions</option>
          <option value="PASS">PASS</option>
          <option value="BLOCK">BLOCK</option>
        </select>
        <input
          type="text"
          placeholder="filter rule_id (multi_turn, ops_mutation…)"
          value={ruleFilter}
          onChange={(e) => setRuleFilter(e.target.value)}
          className="ops-input"
        />
        <button type="button" className="ops-btn ops-btn-ghost" onClick={load}>🔄 Refresh</button>
        <span className="ops-audit-meta">{filtered.length} / {events.length} events</span>
      </div>
      <div className="ops-audit-table-wrap">
        <table className="ops-audit-table">
          <thead>
            <tr>
              <th>ts</th>
              <th>event</th>
              <th>decision</th>
              <th>rule_id</th>
              <th>mode</th>
              <th>provider</th>
              <th>model</th>
              <th>reason</th>
              <th>conversation</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={i} className={`ops-audit-row ops-audit-${e.decision}`}>
                <td className="ops-mono">{(e.ts || "").slice(11, 19)}</td>
                <td>{e.event}</td>
                <td><span className={`ops-badge ops-badge-${e.decision}`}>{e.decision}</span></td>
                <td>{e.rule_id || "—"}</td>
                <td>{e.mode || "—"}</td>
                <td>{e.provider || "—"}</td>
                <td className="ops-mono">{e.model ? e.model.slice(0, 20) : "—"}</td>
                <td>{e.reason ? String(e.reason).slice(0, 60) : "—"}</td>
                <td className="ops-mono">{e.conversation_id ? e.conversation_id.slice(0, 8) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// IMPLANTS TAB
// ---------------------------------------------------------------------------
function ImplantsTab({ token }) {
  const [implants, setImplants] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [rawText, setRawText] = useState("");
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testMode, setTestMode] = useState("street");
  const [testMsg, setTestMsg] = useState("Salut, présente-toi.");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const r = await axios.get(`${API}/ops/implants`, { headers: authHeaders(token) });
    setImplants(r.data.implants || []);
    if (!selectedId && r.data.implants?.length) {
      setSelectedId(r.data.implants[0].id);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!implants || !selectedId) return;
    const cur = implants.find((i) => i.id === selectedId);
    if (cur) {
      const { used_by_modes, ...pure } = cur;
      setDraft(JSON.parse(JSON.stringify(pure)));
      setRawText(JSON.stringify(pure, null, 2));
      setIsNew(false);
    }
  }, [selectedId, implants]);

  const selectNew = () => {
    const skeleton = {
      id: "new_implant",
      protocol: "JSONFIRST",
      version: "1.0.0",
      compatible_modes: ["street"],
      enabled: true,
      strict_enforce: false,
      branding: { signature: "NEW_IMPLANT" },
      display_rules: {
        mandatory_start: {
          header: "🆕 New implant activé",
          salutation: "Salut !",
          status_question: "Qu'est-ce qu'on fait ?",
          service_message: "",
        },
      },
      core_logic: {
        step_1_accent_engine: { mandatory: false, rules: [] },
        step_2_geo_context: { location: "Abidjan", vibes: [], currency: "CFA" },
      },
      footer_lock: { mode: "standard", reference: "https://jsonfirst.com" },
    };
    setSelectedId(null);
    setDraft(skeleton);
    setRawText(JSON.stringify(skeleton, null, 2));
    setIsNew(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      let payload;
      try { payload = JSON.parse(rawText); } catch (e) { toast.error("JSON invalide"); setSaving(false); return; }
      if (isNew) {
        await axios.post(`${API}/ops/implants`, payload, { headers: authHeaders(token) });
        toast.success(`Implant ${payload.id} créé`);
      } else {
        await axios.put(`${API}/ops/implants/${payload.id}`, payload, { headers: authHeaders(token) });
        toast.success(`Implant ${payload.id} sauvegardé`);
      }
      setIsNew(false);
      await load();
      setSelectedId(payload.id);
    } catch (e) {
      const errs = e.response?.data?.validation_errors;
      toast.error(errs ? `Erreurs: ${errs.join(", ")}` : (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!draft?.id) return;
    if (!window.confirm(`Supprimer l'implant ${draft.id} ?`)) return;
    try {
      await axios.delete(`${API}/ops/implants/${draft.id}`, { headers: authHeaders(token) });
      toast.success("Implant supprimé");
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const runTest = async () => {
    if (!draft?.id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(
        `${API}/ops/test/implant`,
        { id: draft.id, mode: testMode, message: testMsg },
        { headers: authHeaders(token) }
      );
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.error || e.message });
    } finally { setTesting(false); }
  };

  if (!implants) return <div className="ops-loading">Chargement…</div>;

  return (
    <div className="ops-modes">
      <div className="ops-modes-submenu">
        {implants.map((imp) => (
          <button
            key={imp.id}
            type="button"
            className={`ops-submenu-btn ${selectedId === imp.id && !isNew ? "is-active" : ""}`}
            onClick={() => { setIsNew(false); setSelectedId(imp.id); }}
            data-testid={`ops-implant-tab-${imp.id}`}
          >
            {imp.enabled ? "🟢" : "⚪"} {imp.id} <span className="ops-mono" style={{ opacity: 0.6 }}>v{imp.version}</span>
            {imp.used_by_modes?.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                [{imp.used_by_modes.join(", ")}]
              </span>
            )}
          </button>
        ))}
        <div className="ops-submenu-spacer" />
        <button type="button" className="ops-btn ops-btn-ghost" onClick={selectNew} data-testid="ops-implant-new">
          ➕ Nouveau
        </button>
      </div>

      {draft ? (
        <div className="ops-form">
          <p className="ops-warn" style={{ marginTop: 0 }}>
            💉 Les implants sont des couches comportementales qui shape le prompt LLM (display_rules, accent_engine, footer_lock, branding).
            Édite le JSON complet ci-dessous. Si <code>strict_enforce=true</code>, le serveur force les règles sur la sortie.
          </p>
          <label className="ops-field">
            <span>JSON implant</span>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                try { setDraft(JSON.parse(e.target.value)); } catch (_) {}
              }}
              className="ops-textarea ops-mono"
              rows={26}
              data-testid="ops-implant-json"
            />
          </label>

          <div className="ops-actions">
            <button type="button" className="ops-btn ops-btn-primary" onClick={save} disabled={saving} data-testid="ops-implant-save">
              💾 {saving ? "Sauvegarde…" : (isNew ? "Créer" : "Sauvegarder")}
            </button>
            <button type="button" className="ops-btn ops-btn-ghost" onClick={() => setTestOpen(true)} data-testid="ops-implant-test-btn">
              🧪 Tester implant
            </button>
            {!isNew && (
              <button type="button" className="ops-btn ops-btn-ghost" onClick={del} data-testid="ops-implant-delete">
                🗑️ Supprimer
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="ops-loading">Sélectionne un implant ou crée-en un nouveau.</div>
      )}

      {testOpen && (
        <div className="ops-modal-backdrop" onClick={() => setTestOpen(false)}>
          <div className="ops-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3>🧪 Tester implant — {draft?.id}</h3>
            <label className="ops-field">
              <span>Mode de test</span>
              <select value={testMode} onChange={(e) => setTestMode(e.target.value)} className="ops-input">
                {(draft?.compatible_modes || ["casual", "street", "business"]).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </label>
            <label className="ops-field">
              <span>Message utilisateur</span>
              <textarea
                value={testMsg}
                onChange={(e) => setTestMsg(e.target.value)}
                rows={3}
                className="ops-textarea"
                data-testid="ops-implant-test-msg"
              />
            </label>
            <div className="ops-actions">
              <button type="button" className="ops-btn ops-btn-primary" onClick={runTest} disabled={testing} data-testid="ops-implant-test-run">
                {testing ? "Test en cours…" : "▶ Lancer"}
              </button>
              <button type="button" className="ops-btn ops-btn-ghost" onClick={() => setTestOpen(false)}>Fermer</button>
            </div>
            {testResult && (
              <div style={{ marginTop: 16 }}>
                {testResult.ok ? (
                  <>
                    <div><strong>{testResult.provider}/{testResult.model}</strong> — {testResult.latency_ms}ms</div>
                    <pre className="ops-pre ops-mono" style={{ maxHeight: 240 }}>{testResult.text}</pre>
                    <details>
                      <summary style={{ cursor: "pointer", opacity: 0.7 }}>System prompt augmenté (preview)</summary>
                      <pre className="ops-pre ops-mono" style={{ maxHeight: 200, fontSize: 11 }}>{testResult.augmented_system_prompt_preview}</pre>
                    </details>
                  </>
                ) : (
                  <div style={{ color: "#dc3545" }}>❌ {testResult.error}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// LLM HEALTH TAB — circuit breaker snapshot, manual control, 24h stats
// ---------------------------------------------------------------------------
function LLMHealthTab({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ops/llm/health`, { headers: authHeaders(token) });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const act = async (provider, model, action) => {
    setBusy(`${provider}:${model}:${action}`);
    try {
      if (action === 'test') {
        const r = await axios.post(
          `${API}/ops/llm/test`,
          { provider, model, via: 'emergent', message: 'Dis OK.' },
          { headers: authHeaders(token) }
        );
        if (r.data.ok) toast.success(`${provider}/${model} OK — ${r.data.latency_ms}ms`);
        else toast.error(`${provider}/${model} : ${r.data.error_type || ''} ${r.data.error || ''}`);
      } else if (action === 'open' || action === 'reset') {
        const url = `${API}/ops/llm/circuit/${encodeURIComponent(provider)}/${encodeURIComponent(model)}/${action}`;
        await axios.post(url, {}, { headers: authHeaders(token) });
        toast.success(`Circuit ${action === 'open' ? 'ouvert' : 'fermé'}`);
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <div className="ops-loading" style={{ color: '#dc3545' }}>Erreur : {error}</div>;
  if (!data) return <div className="ops-loading">Chargement…</div>;

  const statusDot = (s) => {
    if (s === 'closed') return { dot: '🟢', label: 'Healthy' };
    if (s === 'degraded') return { dot: '🟡', label: 'Degraded' };
    if (s === 'open') return { dot: '🔴', label: 'Open' };
    if (s === 'manual_open') return { dot: '⛔', label: 'Manual open' };
    return { dot: '⚪', label: s };
  };

  return (
    <div className="ops-modes" data-testid="llm-health-tab">
      <div className="ops-form" style={{ paddingTop: 0 }}>
        <fieldset className="ops-fieldset">
          <legend>📊 Stats — dernières 24h</legend>
          <div className="ops-form-row" style={{ gap: 24, alignItems: 'center' }}>
            <span><strong>Total tentatives :</strong> {data.last_24h_stats.total_attempts}</span>
            <span><strong>Fallback déclenchés :</strong> {data.last_24h_stats.fallback_count}</span>
            <span><strong>Chain exhausted :</strong> {data.last_24h_stats.exhausted_count}</span>
            <button type="button" className="ops-btn ops-btn-ghost" onClick={load} data-testid="ops-llm-health-refresh">
              ↻ Rafraîchir
            </button>
          </div>
        </fieldset>

        <fieldset className="ops-fieldset">
          <legend>🧠 Providers</legend>
          <table className="ops-audit-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Provider</th>
                <th style={{ textAlign: 'left' }}>Model</th>
                <th>Status</th>
                <th>Failures (1min)</th>
                <th style={{ textAlign: 'left' }}>Last error</th>
                <th style={{ textAlign: 'left' }}>Used in</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => {
                const s = statusDot(p.status);
                const k = `${p.provider}:${p.model}`;
                return (
                  <tr key={k} data-testid={`ops-llm-row-${p.provider}-${p.model}`}>
                    <td className="ops-mono">{p.provider}</td>
                    <td className="ops-mono" style={{ wordBreak: 'break-all' }}>{p.model}</td>
                    <td style={{ textAlign: 'center' }} title={s.label}>
                      {s.dot} <small style={{ opacity: 0.7 }}>{p.status}</small>
                      {p.open_until && (
                        <div style={{ fontSize: 10, opacity: 0.6 }}>
                          jusqu'à {new Date(p.open_until).toLocaleTimeString()}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>{p.recent_failures}</td>
                    <td style={{ fontSize: 11, color: p.last_error ? '#dc3545' : 'inherit' }}>
                      {p.last_error ? `${p.last_error.type}${p.last_error.status ? ` ${p.last_error.status}` : ''}` : '—'}
                    </td>
                    <td style={{ fontSize: 11 }}>{(p.used_in_modes || []).join(', ')}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button" className="ops-btn ops-btn-sm"
                        onClick={() => act(p.provider, p.model, 'test')}
                        disabled={busy === `${k}:test`}
                        data-testid={`ops-llm-test-${p.provider}-${p.model}`}
                      >🧪</button>
                      <button
                        type="button" className="ops-btn ops-btn-sm"
                        onClick={() => act(p.provider, p.model, 'open')}
                        disabled={busy === `${k}:open` || p.status === 'manual_open' || p.status === 'open'}
                      >⛔</button>
                      <button
                        type="button" className="ops-btn ops-btn-sm"
                        onClick={() => act(p.provider, p.model, 'reset')}
                        disabled={busy === `${k}:reset`}
                        data-testid={`ops-llm-reset-${p.provider}-${p.model}`}
                      >↻</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ marginTop: 12, opacity: 0.6, fontSize: 12 }}>
            Auto-refresh toutes les 5 secondes. 🟢 healthy · 🟡 degraded · 🔴 circuit ouvert · ⛔ ouvert manuel.
          </p>
        </fieldset>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// WEB SEARCH TAB — Tavily real-time RAG configuration & stats
// ---------------------------------------------------------------------------
function WebSearchTab({ token }) {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testQuery, setTestQuery] = useState("Qui a gagné la CAN 2024 ?");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const refreshRef = useRef(null);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/ops/web_search/config`, { headers: authHeaders(token) });
      setConfig(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data)));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };
  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/ops/web_search/stats`, { headers: authHeaders(token) });
      setStats(r.data);
    } catch (_) {}
  };

  useEffect(() => {
    loadConfig();
    loadStats();
    refreshRef.current = setInterval(loadStats, 8000);
    return () => clearInterval(refreshRef.current);
  }, []); // eslint-disable-line

  if (!config || !draft) return <div className="ops-loading">Chargement…</div>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(config);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!draft.enabled,
        default_mode: draft.default_mode,
        daily_cap: parseInt(draft.daily_cap, 10) || 100,
        trigger_keywords: (draft.trigger_keywords || []).filter((k) => k && k.trim()),
      };
      await axios.put(`${API}/ops/web_search/config`, payload, { headers: authHeaders(token) });
      toast.success("Config Web Search sauvegardée");
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!testQuery.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(
        `${API}/ops/test/tavily`,
        { query: testQuery.trim() },
        { headers: authHeaders(token) }
      );
      setTestResult(r.data);
      if (r.data.ok) toast.success(`🌐 Tavily OK — ${r.data.sources_count} sources, ${r.data.latency_ms}ms`);
      await loadStats();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.error || e.message });
      toast.error(e.response?.data?.error || e.message);
    } finally { setTesting(false); }
  };

  const updateKeyword = (i, value) => {
    const next = [...(draft.trigger_keywords || [])];
    next[i] = value;
    setDraft({ ...draft, trigger_keywords: next });
  };
  const removeKeyword = (i) => {
    const next = (draft.trigger_keywords || []).filter((_, idx) => idx !== i);
    setDraft({ ...draft, trigger_keywords: next });
  };
  const addKeyword = () => {
    setDraft({ ...draft, trigger_keywords: [...(draft.trigger_keywords || []), "\\bnouveau_mot\\b"] });
  };
  const resetDefaults = () => {
    if (!window.confirm("Restaurer les keywords par défaut ?")) return;
    setDraft({
      ...draft,
      trigger_keywords: [
        "\\b(news|actualit[ée]s?|nouvelle[s]?)\\b",
        "\\b(aujourd[''']?hui|hier|demain|ce\\s+(matin|soir|mois|moment)|cette\\s+semaine|en\\s+ce\\s+moment)\\b",
        "\\b(20(2[5-9]|3\\d))\\b",
        "\\b(r[ée]sultat[s]?|score|match[s]?|finale|champion[s]?|vainqueur|gagnant)\\b",
        "\\b([ée]lection[s]?|premier\\s+ministre|pr[ée]sident|gouvernement|ministre)\\b",
        "\\b(m[ée]t[ée]o|temp[ée]rature|pluie)\\b",
        "\\b(cours|prix|tarif|taux|inflation)\\b",
        "\\b(can\\s*\\d{4})\\b",
        "\\bderni[èe]r[es]?\\b",
      ],
    });
  };

  return (
    <div className="ops-modes" data-testid="ops-web-search-tab">
      <div className="ops-form">
        <p className="ops-warn" style={{ marginTop: 0 }}>
          🌐 La recherche web temps réel utilise <strong>Tavily</strong>. Configure la clé dans l'onglet 🔑 Clés API
          (champ <code>tavily_api_key</code>). Cache RAM : 5 minutes par requête. Quand un mot-clé déclencheur est
          détecté (mode <code>auto</code>), AYA fait une recherche avant de répondre et cite les sources.
        </p>

        <div className="ops-ws-grid">
          <fieldset className="ops-fieldset">
            <legend>⚙️ Configuration</legend>
            <div className="ops-form-row" style={{ alignItems: "center" }}>
              <label className="ops-field-inline">
                <input
                  type="checkbox"
                  checked={!!draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                  data-testid="ops-ws-enabled"
                />
                <span style={{ marginLeft: 6 }}><strong>Master switch</strong></span>
              </label>
              <label className="ops-field ops-field-sm">
                <span>Mode par défaut</span>
                <select
                  value={draft.default_mode}
                  onChange={(e) => setDraft({ ...draft, default_mode: e.target.value })}
                  className="ops-input"
                  data-testid="ops-ws-default-mode"
                >
                  <option value="auto">auto (keywords)</option>
                  <option value="on">on (toujours)</option>
                  <option value="off">off (jamais)</option>
                </select>
              </label>
              <label className="ops-field ops-field-sm">
                <span>Daily cap (calls réels)</span>
                <input
                  type="number" min="1" max="10000"
                  value={draft.daily_cap}
                  onChange={(e) => setDraft({ ...draft, daily_cap: parseInt(e.target.value, 10) || 1 })}
                  className="ops-input"
                  data-testid="ops-ws-daily-cap"
                />
              </label>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong style={{ fontSize: 13 }}>🔑 Mots-clés déclencheurs (regex, mode auto)</strong>
              <p style={{ fontSize: 11, opacity: 0.6, margin: "4px 0 8px" }}>
                Une seule correspondance suffit pour déclencher la recherche. Format <code>JavaScript regex</code>, sans flags (insensible à la casse appliqué auto).
              </p>
              <div className="ops-ws-keyword-list">
                {(draft.trigger_keywords || []).map((kw, i) => (
                  <div key={i} className="ops-ws-keyword-row" data-testid={`ops-ws-kw-${i}`}>
                    <input
                      type="text"
                      value={kw}
                      onChange={(e) => updateKeyword(i, e.target.value)}
                      className="ops-input ops-mono"
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="ops-btn ops-btn-sm" onClick={() => removeKeyword(i)}>🗑️</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" className="ops-btn ops-btn-ghost" onClick={addKeyword} data-testid="ops-ws-kw-add">
                  + Ajouter mot-clé
                </button>
                <button type="button" className="ops-btn ops-btn-ghost" onClick={resetDefaults}>
                  ↺ Restaurer défauts
                </button>
              </div>
            </div>

            <div className="ops-actions">
              <button
                type="button"
                className="ops-btn ops-btn-primary"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="ops-ws-save"
              >
                💾 {saving ? "Sauvegarde…" : "Sauvegarder"}
              </button>
              <button
                type="button"
                className="ops-btn ops-btn-ghost"
                onClick={() => setDraft(JSON.parse(JSON.stringify(config)))}
                disabled={!dirty}
              >
                ↺ Annuler
              </button>
            </div>
          </fieldset>

          <fieldset className="ops-fieldset">
            <legend>📊 Stats — dernières 24h</legend>
            {stats ? (
              <div className="ops-ws-stats" data-testid="ops-ws-stats">
                <div className="ops-ws-stat"><span>Total recherches</span><strong>{stats.searches_24h}</strong></div>
                <div className="ops-ws-stat"><span>Calls Tavily réels</span><strong>{stats.real_calls_24h}</strong></div>
                <div className="ops-ws-stat"><span>Cache hits</span><strong>{stats.cache_hits_24h}</strong></div>
                <div className="ops-ws-stat"><span>Daily cap</span><strong>{stats.real_calls_24h} / {stats.daily_cap}</strong></div>
                <div className="ops-ws-stat"><span>Keywords actifs</span><strong>{stats.keyword_count}</strong></div>
                {stats.top_queries?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <strong style={{ fontSize: 12 }}>Top queries</strong>
                    <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 12 }}>
                      {stats.top_queries.slice(0, 5).map((q, i) => (
                        <li key={i}><code>{q.query}</code> ({q.count}×)</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : <div className="ops-loading">Stats…</div>}

            <div style={{ marginTop: 16 }}>
              <strong style={{ fontSize: 13 }}>🧪 Test manuel</strong>
              <div className="ops-form-row" style={{ marginTop: 6 }}>
                <input
                  type="text"
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  className="ops-input"
                  placeholder="Ex: dernières news Côte d'Ivoire"
                  data-testid="ops-ws-test-query"
                />
                <button
                  type="button"
                  className="ops-btn ops-btn-primary"
                  onClick={runTest}
                  disabled={testing || !testQuery.trim()}
                  data-testid="ops-ws-test-run"
                >
                  {testing ? "…" : "▶ Tester"}
                </button>
              </div>
              {testResult && (
                <div className="ops-ws-test-result" data-testid="ops-ws-test-result">
                  {testResult.ok ? (
                    <>
                      <div><strong>✅ {testResult.sources_count} sources</strong> · {testResult.latency_ms}ms</div>
                      {testResult.answer_preview && (
                        <div style={{ marginTop: 6, opacity: 0.8 }}>
                          <em>Synthèse :</em> {testResult.answer_preview}
                        </div>
                      )}
                      <ol style={{ margin: "8px 0 0 16px", padding: 0, fontSize: 12 }}>
                        {(testResult.sources || []).map((s, i) => (
                          <li key={i} style={{ marginBottom: 4 }}>
                            <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1d4ed8" }}>
                              {s.title || s.url}
                            </a>
                            {s.published && <span style={{ opacity: 0.6 }}> · {String(s.published).slice(0, 10)}</span>}
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : (
                    <div style={{ color: "#dc2626" }}>
                      ❌ {testResult.error}
                      {testResult.upstream_status && <span> (HTTP {testResult.upstream_status})</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </fieldset>
        </div>

        {stats && stats.last_50?.length > 0 && (
          <fieldset className="ops-fieldset" style={{ marginTop: 16 }}>
            <legend>📜 Historique récent (50 dernières)</legend>
            <div className="ops-ws-history" data-testid="ops-ws-history">
              <div className="ops-ws-history-row" style={{ fontWeight: 600, opacity: 0.7 }}>
                <span>Heure</span><span>Query</span><span>Durée / état</span><span>Sources</span>
              </div>
              {stats.last_50.map((h, i) => {
                const ts = new Date(h.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                const cls = h.error ? "is-error" : (h.cache_hit ? "is-cache" : "");
                return (
                  <div key={i} className={`ops-ws-history-row ${cls}`}>
                    <span className="ops-mono">{ts}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.query}</span>
                    <span>
                      {h.error ? <span style={{ color: "#dc2626" }}>{h.error}</span>
                        : h.cache_hit ? <span style={{ color: "#16a34a" }}>cache</span>
                        : <span>{h.duration_ms}ms</span>}
                    </span>
                    <span style={{ textAlign: "right" }}>{h.sources_count}</span>
                  </div>
                );
              })}
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SPONSORED TAB — manage native ad slots, view stats per slot
// ---------------------------------------------------------------------------
function SponsoredTab({ token }) {
  const [slots, setSlots] = useState(null);
  const [editing, setEditing] = useState(null); // slot or new skeleton
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rawText, setRawText] = useState("");

  const load = async () => {
    const r = await axios.get(`${API}/ops/sponsored`, { headers: authHeaders(token) });
    setSlots(r.data.slots || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (slot) => {
    setIsNew(false);
    setEditing(slot);
    setRawText(JSON.stringify(slot, null, 2));
  };
  const startNew = () => {
    const skel = {
      id: `slot_${Date.now()}`,
      partner_name: "",
      logo_url: "",
      url: "https://",
      headline: "",
      snippet: "",
      cta_label: "Découvrir",
      modes_target: ["casual", "business"],
      categories_target: ["news"],
      active_from: new Date().toISOString(),
      active_to: new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString(),
      weight: 1.0,
      max_impressions_per_day: 1000,
      enabled: true,
    };
    setEditing(skel); setIsNew(true);
    setRawText(JSON.stringify(skel, null, 2));
  };

  const save = async () => {
    setSaving(true);
    try {
      let payload;
      try { payload = JSON.parse(rawText); } catch (e) { toast.error("JSON invalide"); setSaving(false); return; }
      if (isNew) await axios.post(`${API}/ops/sponsored`, payload, { headers: authHeaders(token) });
      else await axios.put(`${API}/ops/sponsored/${payload.id}`, payload, { headers: authHeaders(token) });
      toast.success(`Slot ${payload.id} sauvegardé`);
      setEditing(null); setIsNew(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(`Supprimer le slot ${id} ?`)) return;
    try {
      await axios.delete(`${API}/ops/sponsored/${id}`, { headers: authHeaders(token) });
      toast.success("Slot supprimé");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  if (!slots) return <div className="ops-loading">Chargement…</div>;

  return (
    <div className="ops-modes" data-testid="ops-sponsored-tab">
      <div className="ops-form">
        <p className="ops-warn" style={{ marginTop: 0 }}>
          🎯 Les slots sponsorisés sont injectés en metadata <strong>UI-only</strong> (le LLM ne les voit jamais). Ciblage par mode + catégorie détectée dans le message.
          Compteurs d'impressions / clics persistés par jour.
        </p>

        <div className="ops-actions" style={{ marginTop: 0 }}>
          <button type="button" className="ops-btn ops-btn-primary" onClick={startNew} data-testid="ops-sponsored-new">
            ➕ Nouveau slot
          </button>
        </div>

        <fieldset className="ops-fieldset">
          <legend>📋 Slots ({slots.length})</legend>
          <div className="ops-sponsored-grid is-header">
            <span>Partenaire / ID</span>
            <span>Modes</span>
            <span>Catégories</span>
            <span>Impr.</span>
            <span>Clics</span>
            <span>CTR</span>
            <span>Actions</span>
          </div>
          {slots.length === 0 && <p style={{ opacity: 0.6, marginTop: 8 }}>Aucun slot. Crée le premier ci-dessus.</p>}
          {slots.map((s) => {
            const ctr = s.impression_count > 0 ? ((s.click_count / s.impression_count) * 100).toFixed(2) : "0.00";
            return (
              <div key={s.id} className="ops-sponsored-grid" data-testid={`ops-sponsored-row-${s.id}`}>
                <span>
                  <strong>{s.partner_name || "(sans nom)"}</strong><br />
                  <code style={{ fontSize: 10, opacity: 0.6 }}>{s.id}</code>
                  {!s.enabled && <span style={{ color: "#dc2626", marginLeft: 4 }}>· OFF</span>}
                </span>
                <span style={{ fontSize: 11 }}>{(s.modes_target || []).join(", ") || "(tous)"}</span>
                <span style={{ fontSize: 11 }}>{(s.categories_target || []).join(", ") || "(toutes)"}</span>
                <span>{s.impression_count || 0}</span>
                <span>{s.click_count || 0}</span>
                <span style={{ color: "#16a34a", fontWeight: 600 }}>{ctr}%</span>
                <span>
                  <button type="button" className="ops-btn ops-btn-sm" onClick={() => startEdit(s)} data-testid={`ops-sponsored-edit-${s.id}`}>✏️</button>
                  <button type="button" className="ops-btn ops-btn-sm" onClick={() => del(s.id)}>🗑️</button>
                </span>
              </div>
            );
          })}
        </fieldset>

        {editing && (
          <fieldset className="ops-fieldset">
            <legend>{isNew ? "➕ Nouveau slot" : `✏️ Édition — ${editing.id}`}</legend>
            <p style={{ fontSize: 11, opacity: 0.6, marginTop: 0 }}>
              Édite le JSON complet. Modes valides : casual, street, business. Catégories : news, mobile_money, marché, mode, beauté, tech, sport, culture, business, abidjan_vibes.
            </p>
            <label className="ops-field">
              <span>JSON slot</span>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                className="ops-textarea ops-mono"
                rows={20}
                data-testid="ops-sponsored-json"
              />
            </label>
            <div className="ops-actions">
              <button type="button" className="ops-btn ops-btn-primary" onClick={save} disabled={saving} data-testid="ops-sponsored-save">
                💾 {saving ? "Sauvegarde…" : (isNew ? "Créer" : "Sauvegarder")}
              </button>
              <button type="button" className="ops-btn ops-btn-ghost" onClick={() => { setEditing(null); setIsNew(false); }}>Annuler</button>
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PAYMENTS TAB — MOCK CinetPay transactions list + revenue stats
// ---------------------------------------------------------------------------
function PaymentsTab({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const refRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ops/payments`, { headers: authHeaders(token) });
      setData(r.data);
    } catch (_) {}
  };
  useEffect(() => {
    load();
    refRef.current = setInterval(load, 8000);
    return () => clearInterval(refRef.current);
  }, []); // eslint-disable-line

  if (!data) return <div className="ops-loading">Chargement…</div>;
  const { stats, transactions } = data;
  const filtered = filter === "all" ? transactions : transactions.filter((t) => t.status === filter);

  return (
    <div className="ops-modes" data-testid="ops-payments-tab">
      <div className="ops-form">
        <p className="ops-warn" style={{ marginTop: 0 }}>
          💳 <strong>MOCK CinetPay</strong> · provider <code>{stats?.total_tx > 0 ? "MOCK_CINETPAY" : "MOCK_CINETPAY"}</code>.
          Aucun débit réel. Migration vers vraie API CinetPay → renseigne la clé puis swap le store.
        </p>

        <fieldset className="ops-fieldset">
          <legend>📊 Stats globales</legend>
          <div className="ops-pay-stats-grid">
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Revenue 24h</div>
              <div className="ops-pay-stat-value is-revenue">{stats.revenue_fcfa_24h.toLocaleString("fr-FR")} FCFA</div>
            </div>
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Revenue total</div>
              <div className="ops-pay-stat-value is-revenue">{stats.revenue_fcfa_all_time.toLocaleString("fr-FR")} FCFA</div>
            </div>
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Tx 24h</div>
              <div className="ops-pay-stat-value">{stats.tx_24h}</div>
            </div>
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Paid 24h</div>
              <div className="ops-pay-stat-value">{stats.paid_count_24h}</div>
            </div>
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Failed 24h</div>
              <div className="ops-pay-stat-value">{stats.failed_count_24h}</div>
            </div>
            <div className="ops-pay-stat-card">
              <div className="ops-pay-stat-label">Total tx</div>
              <div className="ops-pay-stat-value">{stats.total_tx}</div>
            </div>
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
            <div>
              <strong>Par méthode :</strong>{" "}
              {Object.entries(stats.by_method || {}).length === 0 ? "—" :
                Object.entries(stats.by_method).map(([m, c]) => <span key={m} style={{ marginRight: 8 }}><code>{m}</code>: {c}</span>)}
            </div>
            <div>
              <strong>Par purpose :</strong>{" "}
              {Object.entries(stats.by_purpose || {}).length === 0 ? "—" :
                Object.entries(stats.by_purpose).map(([p, c]) => <span key={p} style={{ marginRight: 8 }}><code>{p}</code>: {c}</span>)}
            </div>
          </div>
          {stats.top_users?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <strong>Top users :</strong>{" "}
              {stats.top_users.slice(0, 5).map((u) => <span key={u.userId} style={{ marginRight: 8 }}><code>{u.userId.slice(0, 8)}</code>: {u.count}</span>)}
            </div>
          )}
        </fieldset>

        <fieldset className="ops-fieldset">
          <legend>📜 Transactions ({filtered.length} affichées)</legend>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ops-input" style={{ width: 180 }}>
              <option value="all">Tous statuts</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <button type="button" className="ops-btn ops-btn-ghost" onClick={load}>↻ Refresh</button>
          </div>
          <div className="ops-audit-table-wrap">
            <table className="ops-audit-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>tx_id</th>
                  <th>user</th>
                  <th>amount</th>
                  <th>method</th>
                  <th>purpose</th>
                  <th>status</th>
                  <th>created</th>
                  <th>paid</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan="8" style={{ opacity: 0.6, padding: 12 }}>Aucune transaction.</td></tr>}
                {filtered.map((t) => (
                  <tr key={t.id} data-testid={`ops-pay-row-${t.id}`}>
                    <td className="ops-mono" style={{ fontSize: 10 }}>{t.id.slice(0, 16)}…</td>
                    <td className="ops-mono" style={{ fontSize: 11 }}>{(t.userId || "—").slice(0, 12)}</td>
                    <td><strong>{t.amount_fcfa} FCFA</strong></td>
                    <td>{t.payment_method || "—"}</td>
                    <td>{t.purpose}</td>
                    <td>
                      <span className={`ops-badge ops-badge-${t.status === "paid" ? "PASS" : t.status === "failed" ? "BLOCK" : "PASS"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="ops-mono" style={{ fontSize: 10 }}>{(t.created_at || "").slice(11, 19)}</td>
                    <td className="ops-mono" style={{ fontSize: 10 }}>{t.paid_at ? t.paid_at.slice(11, 19) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NEWSPAPERS TAB — manage CIV news source list
// ---------------------------------------------------------------------------
function NewspapersTab({ token }) {
  const [list, setList] = useState(null);
  const [categories, setCategories] = useState({});
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState({ id: "", name: "", category: "news", type: "newspaper", domain: "", url: "", description: "", physical_address: "", google_maps_query: "", premium_enabled: true, free_enabled: true, active: true });

  const CATEGORIES = ["news", "mode", "beauté", "marché", "tech", "sport", "culture", "mobile_money", "business", "abidjan_vibes"];
  const TYPES = ["newspaper", "brand", "marketplace", "blog", "operator", "magazine", "organization", "festival"];

  const load = async () => {
    const r = await axios.get(`${API}/ops/partners`, { headers: authHeaders(token) });
    setList(r.data.partners || []);
    setCategories(r.data.categories || {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!draft.id || !draft.name || !draft.domain) { toast.error("id, name, domain requis"); return; }
    try {
      await axios.post(`${API}/ops/partners`, draft, { headers: authHeaders(token) });
      toast.success(`Partenaire ${draft.name} créé`);
      setDraft({ id: "", name: "", category: "news", type: "newspaper", domain: "", url: "", description: "", physical_address: "", google_maps_query: "", premium_enabled: true, free_enabled: true, active: true });
      setAdding(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const upd = async (id, patch) => {
    try {
      await axios.put(`${API}/ops/partners/${id}`, patch, { headers: authHeaders(token) });
      await load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm(`Supprimer ${id} ?`)) return;
    try {
      await axios.delete(`${API}/ops/partners/${id}`, { headers: authHeaders(token) });
      toast.success("Supprimé");
      await load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  if (!list) return <div className="ops-loading">Chargement…</div>;

  const filtered = filter === "all" ? list : list.filter((p) => p.category === filter);

  return (
    <div className="ops-modes" data-testid="ops-partners-tab">
      <div className="ops-form">
        <p className="ops-warn" style={{ marginTop: 0 }}>
          🤝 Registre multi-catégories des partenaires CIV (journaux, marques, marketplaces, opérateurs, magazines…). Le clic "Lire"
          déclenche une recherche Tavily scopée au domain. Le clic "💰 Premium" ouvre la modal de paiement MOCK + résumé structuré.
          Les entrées <code>verification_pending</code> sont à confirmer manuellement.
        </p>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Filtrer :</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="ops-input"
            style={{ width: 240 }}
            data-testid="ops-partners-filter"
          >
            <option value="all">Toutes catégories ({list.length})</option>
            {CATEGORIES.map((c) => {
              const stats = categories[c] || { total: 0, active: 0 };
              return <option key={c} value={c}>{c} ({stats.active}/{stats.total} actifs)</option>;
            })}
          </select>
          <button type="button" className="ops-btn ops-btn-primary" onClick={() => setAdding((v) => !v)} data-testid="ops-np-new-btn">
            {adding ? "✕ Annuler" : "➕ Ajouter partenaire"}
          </button>
        </div>

        {adding && (
          <fieldset className="ops-fieldset">
            <legend>➕ Nouveau partenaire</legend>
            <div className="ops-form-row">
              <label className="ops-field">
                <span>id (slug)</span>
                <input type="text" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} className="ops-input ops-mono" data-testid="ops-np-new-id" />
              </label>
              <label className="ops-field">
                <span>name</span>
                <input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="ops-input" data-testid="ops-np-new-name" />
              </label>
              <label className="ops-field">
                <span>category</span>
                <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="ops-input" data-testid="ops-np-new-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="ops-field">
                <span>type</span>
                <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="ops-input">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="ops-field">
                <span>domain (sans https)</span>
                <input type="text" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} className="ops-input ops-mono" placeholder="exemple.ci" data-testid="ops-np-new-domain" />
              </label>
              <label className="ops-field">
                <span>description</span>
                <input type="text" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="ops-input" />
              </label>
              <label className="ops-field">
                <span>physical_address (optionnel)</span>
                <input type="text" value={draft.physical_address} onChange={(e) => setDraft({ ...draft, physical_address: e.target.value })} className="ops-input" placeholder="ex: Cocody Riviera Palmeraie, Abidjan" data-testid="ops-np-new-address" />
              </label>
              <label className="ops-field">
                <span>google_maps_query (optionnel)</span>
                <input type="text" value={draft.google_maps_query} onChange={(e) => setDraft({ ...draft, google_maps_query: e.target.value })} className="ops-input" placeholder="Si vide, sera composé avec le nom + l'adresse" data-testid="ops-np-new-mapsq" />
              </label>
            </div>
            <div className="ops-actions">
              <button type="button" className="ops-btn ops-btn-primary" onClick={create} data-testid="ops-np-create">Créer</button>
            </div>
          </fieldset>
        )}

        <fieldset className="ops-fieldset">
          <legend>📋 Partenaires ({filtered.length}{filter !== "all" ? ` · cat: ${filter}` : ""})</legend>
          <table className="ops-audit-table" style={{ width: "100%" }}>
            <thead>
              <tr><th>Logo</th><th>Nom / id</th><th>Catégorie</th><th>Type</th><th>Domain</th><th>Free</th><th>Premium</th><th>Actif</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map((n) => (
                <tr key={n.id} data-testid={`ops-np-row-${n.id}`}>
                  <td>{n.logo_url ? <img src={n.logo_url} alt={n.name} style={{ width: 22, height: 22 }} /> : "—"}</td>
                  <td>
                    <strong>{n.name}</strong>
                    {n.verification_pending && <span title="À vérifier" style={{ marginLeft: 6, color: "#dc2626" }}>⚠️</span>}
                    <br /><code style={{ fontSize: 10, opacity: 0.6 }}>{n.id}</code>
                  </td>
                  <td><code style={{ fontSize: 11 }}>{n.category}</code></td>
                  <td><code style={{ fontSize: 10, opacity: 0.7 }}>{n.type}</code></td>
                  <td className="ops-mono" style={{ fontSize: 11 }}>
                    <a href={n.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1d4ed8" }}>{n.domain}</a>
                    {n.physical_address ? (
                      <span title={n.physical_address} style={{ marginLeft: 6, fontSize: 11 }}>📍</span>
                    ) : (
                      <span title="Pas d'adresse" style={{ marginLeft: 6, fontSize: 11, opacity: 0.5 }}>🌐</span>
                    )}
                    {Number.isFinite(n.click_count) && n.click_count > 0 && (
                      <span title="Clics enregistrés" style={{ marginLeft: 6, fontSize: 10, color: "#64748b" }}>· {n.click_count} 👆</span>
                    )}
                  </td>
                  <td><input type="checkbox" checked={n.free_enabled !== false} onChange={(e) => upd(n.id, { free_enabled: e.target.checked })} /></td>
                  <td><input type="checkbox" checked={n.premium_enabled !== false} onChange={(e) => upd(n.id, { premium_enabled: e.target.checked })} /></td>
                  <td><input type="checkbox" checked={n.active !== false} onChange={(e) => upd(n.id, { active: e.target.checked })} /></td>
                  <td><button type="button" className="ops-btn ops-btn-sm" onClick={() => del(n.id)}>🗑️</button></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="9" style={{ opacity: 0.6, padding: 12 }}>Aucun partenaire dans cette catégorie.</td></tr>}
            </tbody>
          </table>
        </fieldset>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// LEXICONS TAB — manage curated multilingual vocabularies + test pipeline
// ---------------------------------------------------------------------------
function LexiconsTab({ token }) {
  const [list, setList] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [lex, setLex] = useState(null); // full lexicon
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [newEntry, setNewEntry] = useState({ fr: "", dyu: "", domain: "general" });
  const [newVerbatim, setNewVerbatim] = useState("");
  const [testInput, setTestInput] = useState("Bonjour, je vais au marché de Yopougon avec Wave.");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadList = async () => {
    const r = await axios.get(`${API}/ops/lexicons`, { headers: authHeaders(token) });
    setList(r.data.lexicons || []);
    if (!activeId && r.data.lexicons?.[0]) setActiveId(r.data.lexicons[0].id);
  };
  const loadLex = async (id) => {
    const r = await axios.get(`${API}/ops/lexicons/${id}`, { headers: authHeaders(token) });
    setLex(r.data.lexicon);
  };
  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/ops/translations/stats`, { headers: authHeaders(token) });
      setStats(r.data);
    } catch (_) {}
  };

  useEffect(() => { loadList(); loadStats(); }, []); // eslint-disable-line
  useEffect(() => { if (activeId) loadLex(activeId); }, [activeId]); // eslint-disable-line

  const addEntry = async () => {
    if (!newEntry.fr.trim() || !newEntry.dyu.trim()) { toast.error("FR + cible requis"); return; }
    try {
      await axios.post(`${API}/ops/lexicons/${activeId}/entries`, newEntry, { headers: authHeaders(token) });
      toast.success(`Entrée ajoutée : ${newEntry.fr}`);
      setNewEntry({ fr: "", dyu: "", domain: newEntry.domain });
      await loadLex(activeId); await loadList();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const removeEntry = async (fr) => {
    try {
      await axios.delete(`${API}/ops/lexicons/${activeId}/entries/${encodeURIComponent(fr)}`, { headers: authHeaders(token) });
      await loadLex(activeId); await loadList();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const addVerbatim = async () => {
    if (!newVerbatim.trim() || !lex) return;
    const next = { ...lex, preserve_verbatim: [...(lex.preserve_verbatim || []), newVerbatim.trim()] };
    try {
      await axios.put(`${API}/ops/lexicons/${activeId}`, next, { headers: authHeaders(token) });
      setNewVerbatim("");
      await loadLex(activeId); await loadList();
      toast.success(`Verbatim ajouté : ${newVerbatim}`);
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const removeVerbatim = async (term) => {
    const next = { ...lex, preserve_verbatim: (lex.preserve_verbatim || []).filter((t) => t !== term) };
    try {
      await axios.put(`${API}/ops/lexicons/${activeId}`, next, { headers: authHeaders(token) });
      await loadLex(activeId); await loadList();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };
  const runTest = async () => {
    if (!testInput.trim() || !lex) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/ops/translate/test`,
        { text: testInput, target_lang: lex.language_code, lexicon_id: lex.id },
        { headers: authHeaders(token), timeout: 60000 });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`🌍 Trad OK — ${r.data.nllb_latency_ms}ms${r.data.cache_hit ? " (cache)" : ""}`);
      else toast.error(r.data.error);
      await loadStats();
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
      toast.error(e.message);
    } finally { setTesting(false); }
  };

  const exportJSON = () => {
    if (!lex) return;
    const blob = new Blob([JSON.stringify(lex, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${lex.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const importJSON = async (file) => {
    if (!file || !lex) return;
    const text = await file.text();
    try {
      const obj = JSON.parse(text);
      await axios.put(`${API}/ops/lexicons/${activeId}`, obj, { headers: authHeaders(token) });
      toast.success("Lexique importé");
      await loadLex(activeId); await loadList();
    } catch (e) { toast.error("JSON invalide ou erreur API: " + e.message); }
  };

  if (!list) return <div className="ops-loading">Chargement…</div>;

  const filtered = lex
    ? (lex.entries || []).filter((e) => {
        const s = search.toLowerCase();
        return !s || e.fr.toLowerCase().includes(s) || e.dyu.toLowerCase().includes(s) || (e.domain || '').toLowerCase().includes(s);
      })
    : [];

  return (
    <div className="ops-modes" data-testid="ops-lexicons-tab">
      <div className="ops-form">
        <p className="ops-warn" style={{ marginTop: 0 }}>
          🌍 Les lexiques pilotent la pipeline de traduction. Les <code>preserve_verbatim</code> sont protégés par placeholders avant NLLB
          puis restaurés. Les <code>entries</code> servent d'overrides curés post-traduction (forcent les termes CIV).
          {stats && <> · <strong>{stats.translations_24h}</strong> traductions 24h · cache hit ratio <strong>{stats.translations_24h ? Math.round(stats.cache_hits_24h / stats.translations_24h * 100) : 0}%</strong> · avg <strong>{stats.avg_latency_ms}ms</strong></>}
        </p>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Lexique :</span>
          <select
            value={activeId || ""}
            onChange={(e) => setActiveId(e.target.value)}
            className="ops-input"
            data-testid="ops-lex-select"
            style={{ width: 280 }}
          >
            {list.map((l) => (
              <option key={l.id} value={l.id}>
                {l.language_label} ({l.entries_count} entrées · {l.preserve_count} verbatim)
              </option>
            ))}
          </select>
        </div>

        {lex && (
          <>
            <fieldset className="ops-fieldset">
              <legend>📝 Métadonnées</legend>
              <div className="ops-form-row">
                <label className="ops-field"><span>id</span><input type="text" value={lex.id} disabled className="ops-input ops-mono" /></label>
                <label className="ops-field"><span>language_code</span><input type="text" value={lex.language_code} disabled className="ops-input ops-mono" /></label>
                <label className="ops-field"><span>language_label</span><input type="text" value={lex.language_label} disabled className="ops-input" /></label>
                <label className="ops-field"><span>country / version</span><input type="text" value={`${lex.country} · ${lex.version}`} disabled className="ops-input ops-mono" /></label>
              </div>
              <div className="ops-actions">
                <button type="button" className="ops-btn ops-btn-ghost" onClick={exportJSON}>📤 Exporter JSON</button>
                <label className="ops-btn ops-btn-ghost" style={{ cursor: "pointer" }}>
                  📥 Importer JSON
                  <input type="file" accept=".json" style={{ display: "none" }} onChange={(e) => importJSON(e.target.files?.[0])} />
                </label>
              </div>
            </fieldset>

            <fieldset className="ops-fieldset">
              <legend>🛡️ Termes préservés ({(lex.preserve_verbatim || []).length})</legend>
              <p style={{ fontSize: 11, opacity: 0.6, marginTop: 0 }}>Toponymes, marques, organisations à NE PAS traduire. Match insensible à la casse, frontière de mot.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {(lex.preserve_verbatim || []).map((t) => (
                  <span key={t} className="civ-cat-chip-card" style={{ width: "auto", flexDirection: "row", padding: "4px 10px", fontSize: 12, gap: 6 }}>
                    {t}
                    <button type="button" onClick={() => removeVerbatim(t)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#dc2626" }}>×</button>
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  type="text" value={newVerbatim}
                  onChange={(e) => setNewVerbatim(e.target.value)}
                  className="ops-input" placeholder="Ex: Tidjane Thiam"
                  data-testid="ops-lex-verbatim-input"
                />
                <button type="button" className="ops-btn ops-btn-primary" onClick={addVerbatim}>+ Ajouter</button>
              </div>
            </fieldset>

            <fieldset className="ops-fieldset">
              <legend>📚 Entrées ({filtered.length}/{(lex.entries || []).length})</legend>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input
                  type="text" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher fr / dyu / domain…"
                  className="ops-input"
                  style={{ flex: 1 }}
                  data-testid="ops-lex-search"
                />
              </div>
              <table className="ops-audit-table" style={{ width: "100%" }}>
                <thead>
                  <tr><th>Français</th><th>{lex.language_label}</th><th>Domain</th><th></th></tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.fr} data-testid={`ops-lex-entry-${e.fr.replace(/\s/g, '_')}`}>
                      <td>{e.fr}</td>
                      <td className="ops-mono"><strong>{e.dyu}</strong></td>
                      <td><code style={{ fontSize: 10 }}>{e.domain || 'general'}</code></td>
                      <td><button type="button" className="ops-btn ops-btn-sm" onClick={() => removeEntry(e.fr)}>🗑️</button></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan="4" style={{ opacity: 0.6, padding: 12 }}>Aucune entrée.</td></tr>}
                </tbody>
              </table>

              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 120px auto", gap: 6, alignItems: "end" }}>
                <label className="ops-field"><span>Français</span>
                  <input type="text" value={newEntry.fr} onChange={(e) => setNewEntry({ ...newEntry, fr: e.target.value })} className="ops-input" data-testid="ops-lex-new-fr" />
                </label>
                <label className="ops-field"><span>{lex.language_label}</span>
                  <input type="text" value={newEntry.dyu} onChange={(e) => setNewEntry({ ...newEntry, dyu: e.target.value })} className="ops-input ops-mono" data-testid="ops-lex-new-target" />
                </label>
                <label className="ops-field"><span>domain</span>
                  <input type="text" value={newEntry.domain} onChange={(e) => setNewEntry({ ...newEntry, domain: e.target.value })} className="ops-input" />
                </label>
                <button type="button" className="ops-btn ops-btn-primary" onClick={addEntry} data-testid="ops-lex-add-entry">+ Ajouter</button>
              </div>
            </fieldset>

            <fieldset className="ops-fieldset">
              <legend>🧪 Tester la pipeline de traduction</legend>
              <div className="ops-form-row">
                <input
                  type="text" value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  className="ops-input" style={{ flex: 1 }}
                  data-testid="ops-lex-test-input"
                />
                <button type="button" className="ops-btn ops-btn-primary" onClick={runTest} disabled={testing} data-testid="ops-lex-test-run">
                  {testing ? "…" : "▶ Tester"}
                </button>
              </div>
              {testResult && (
                <div className="civ-pay-mock-note" style={{ marginTop: 10, textAlign: "left" }} data-testid="ops-lex-test-result">
                  {testResult.ok ? (
                    <>
                      <div><strong>📥 Original FR :</strong> {testResult.original}</div>
                      <div style={{ marginTop: 6 }}><strong>📤 Traduit ({testResult.target_lang}) :</strong> <code style={{ background: "#fff7ed", padding: "2px 6px", borderRadius: 4 }}>{testResult.translated}</code></div>
                      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>
                        latency: {testResult.nllb_latency_ms}ms · cache: {testResult.cache_hit ? "✓" : "✗"} · placeholders: {testResult.placeholders_count} · model: <code>{testResult.model}</code>
                      </div>
                      {(testResult.applied_entries || []).length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 11 }}>
                          <strong>Lexique appliqué :</strong>{" "}
                          {testResult.applied_entries.map((e) => (
                            <span key={e.fr} style={{ background: "#dcfce7", padding: "2px 6px", borderRadius: 4, marginRight: 4 }}>{e.fr} → {e.target}</span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: "#dc2626" }}>❌ {testResult.error}</div>
                  )}
                </div>
              )}
            </fieldset>

            {stats && stats.last_50?.length > 0 && (
              <fieldset className="ops-fieldset">
                <legend>📜 Dernières traductions ({stats.last_50.length})</legend>
                <div className="ops-ws-history" data-testid="ops-lex-history">
                  {stats.last_50.slice(0, 20).map((h, i) => (
                    <div key={i} className={`ops-ws-history-row ${h.error ? 'is-error' : (h.cache_hit ? 'is-cache' : '')}`}>
                      <span className="ops-mono">{new Date(h.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.text}</span>
                      <span>{h.error ? <span style={{ color: "#dc2626" }}>{h.error}</span> : h.cache_hit ? <span style={{ color: "#16a34a" }}>cache</span> : <span>{h.latency_ms}ms</span>}</span>
                      <span style={{ textAlign: "right", fontSize: 10 }}>{h.src}→{h.tgt}</span>
                    </div>
                  ))}
                </div>
              </fieldset>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ===========================================================================
// v1.13.0 — Sponsored Recommendations tab (CRUD entries, categories, schema,
// config, trigger test). Compact yet operational UI.
// ===========================================================================
function SponsoredRecsTab({ token }) {
  const [section, setSection] = useState("entries");
  return (
    <div className="ops-section" data-testid="ops-sponsored-recs">
      <h2>🤝 Recommandations Partenaires</h2>
      <div className="ops-subtabs" style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          ["entries", "📋 Entrées"],
          ["categories", "🗂️ Catégories"],
          ["schema", "🧩 Schéma"],
          ["config", "⚙️ Config & Test"],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`ops-btn ${section === k ? "ops-btn-primary" : "ops-btn-ghost"}`}
            onClick={() => setSection(k)}
            data-testid={`ops-recs-sub-${k}`}
          >{l}</button>
        ))}
      </div>
      {section === "entries"   && <RecsEntriesPanel token={token} />}
      {section === "categories" && <RecsCategoriesPanel token={token} />}
      {section === "schema"    && <RecsSchemaPanel token={token} />}
      {section === "config"    && <RecsConfigPanel token={token} />}
    </div>
  );
}

function RecsEntriesPanel({ token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // entry or {}
  const [fields, setFields] = useState([]);

  const reload = async () => {
    const params = { page, page_size: 20 };
    if (q) params.q = q;
    if (categorie) params.categorie = categorie;
    if (activeFilter) params.active = activeFilter;
    const r = await axios.get(`${API}/ops/sponsored`, { headers: authHeaders(token), params });
    setItems(r.data.items || []); setTotal(r.data.total || 0);
  };
  useEffect(() => {
    axios.get(`${API}/ops/sponsored/categories`, { headers: authHeaders(token) }).then(r => setCats(r.data.categories || [])).catch(()=>{});
    axios.get(`${API}/ops/sponsored/schema/fields`, { headers: authHeaders(token) }).then(r => setFields(r.data.fields || [])).catch(()=>{});
  }, [token]);
  useEffect(() => { reload(); }, [page, categorie, activeFilter]); // eslint-disable-line

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = editing;
    try {
      if (data.id) await axios.put(`${API}/ops/sponsored/${data.id}`, data, { headers: authHeaders(token) });
      else await axios.post(`${API}/ops/sponsored`, data, { headers: authHeaders(token) });
      toast.success(data.id ? "Entrée mise à jour" : "Entrée créée");
      setEditing(null); reload();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.error || err.message));
    }
  };
  const toggleActive = async (it) => {
    await axios.put(`${API}/ops/sponsored/${it.id}`, { active: !it.active }, { headers: authHeaders(token) });
    reload();
  };
  const onDelete = async (it) => {
    if (!window.confirm(`Désactiver « ${it.nom} » ?`)) return;
    await axios.delete(`${API}/ops/sponsored/${it.id}`, { headers: authHeaders(token) });
    reload();
  };

  const subOpts = (() => {
    const c = cats.find(c => c.id === (editing?.categorie || categorie));
    return c ? c.subcategories : [];
  })();

  return (
    <div data-testid="recs-entries-panel">
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&reload()} placeholder="🔍 Recherche nom/USP/commune" className="ops-input" data-testid="recs-search-input" />
        <select value={categorie} onChange={e=>{setCategorie(e.target.value); setPage(1);}} className="ops-input" data-testid="recs-filter-cat">
          <option value="">Toutes catégories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={activeFilter} onChange={e=>{setActiveFilter(e.target.value); setPage(1);}} className="ops-input">
          <option value="">Tous</option>
          <option value="true">Actifs</option>
          <option value="false">Inactifs</option>
        </select>
        <button type="button" className="ops-btn ops-btn-primary" onClick={() => setEditing({ categorie: cats[0]?.id || "restauration", priority: 5, active: true, custom_fields: {} })} data-testid="recs-new-btn">+ Nouvelle entrée</button>
      </div>
      <div className="ops-meta" data-testid="recs-total">{total} entrée(s)</div>
      <table className="ops-table" data-testid="recs-table">
        <thead><tr><th>Nom</th><th>Catégorie</th><th>Commune</th><th>Prix</th><th>Pri</th><th>Actif</th><th>Actions</th></tr></thead>
        <tbody>
          {items.map(it => (
            <tr key={it.id} data-testid={`recs-row-${it.id}`}>
              <td><strong>{it.nom}</strong><br/><small style={{color:"#64748b"}}>{it.usp}</small></td>
              <td>{it.categorie}<br/><small>{it.sous_categorie || "—"}</small></td>
              <td>{it.commune}</td>
              <td>{it.prix_moyen_fcfa ? it.prix_moyen_fcfa.toLocaleString("fr-FR")+" FCFA" : "—"}</td>
              <td>{it.priority}</td>
              <td><input type="checkbox" checked={!!it.active} onChange={()=>toggleActive(it)} data-testid={`recs-toggle-${it.id}`} /></td>
              <td>
                <button type="button" className="ops-btn ops-btn-ghost" onClick={()=>setEditing({...it})}>✏️</button>
                <button type="button" className="ops-btn ops-btn-ghost" onClick={()=>onDelete(it)}>🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" className="ops-btn ops-btn-ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Préc.</button>
        <span className="ops-meta">Page {page} / {Math.max(1, Math.ceil(total/20))}</span>
        <button type="button" className="ops-btn ops-btn-ghost" disabled={page*20>=total} onClick={()=>setPage(p=>p+1)}>Suiv. →</button>
      </div>
      {editing && (
        <div className="ops-modal-overlay" onClick={(e)=> e.target===e.currentTarget && setEditing(null)} data-testid="recs-edit-modal">
          <form className="ops-modal" onSubmit={onSubmit} style={{ maxWidth: 720 }}>
            <h3>{editing.id ? "Éditer" : "Créer"} une entrée</h3>
            <div className="ops-grid-2">
              <label>Nom *<input required value={editing.nom||""} onChange={e=>setEditing({...editing, nom:e.target.value})} className="ops-input" data-testid="recs-edit-nom" /></label>
              <label>Catégorie *
                <select required value={editing.categorie||""} onChange={e=>setEditing({...editing, categorie:e.target.value, sous_categorie:""})} className="ops-input">
                  {cats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <label>Sous-catégorie
                <select value={editing.sous_categorie||""} onChange={e=>setEditing({...editing, sous_categorie:e.target.value})} className="ops-input">
                  <option value="">—</option>
                  {subOpts.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label>Commune<input value={editing.commune||""} onChange={e=>setEditing({...editing, commune:e.target.value})} className="ops-input" /></label>
              <label>Ville<input value={editing.ville||""} onChange={e=>setEditing({...editing, ville:e.target.value})} className="ops-input" /></label>
              <label>Téléphone<input value={editing.telephone||""} onChange={e=>setEditing({...editing, telephone:e.target.value})} className="ops-input" /></label>
              <label style={{ gridColumn: "1 / -1" }}>Adresse<input value={editing.adresse||""} onChange={e=>setEditing({...editing, adresse:e.target.value})} className="ops-input" /></label>
              <label style={{ gridColumn: "1 / -1" }}>USP / sous-titre<input value={editing.usp||""} onChange={e=>setEditing({...editing, usp:e.target.value})} className="ops-input" /></label>
              <label>Horaires<input value={editing.horaires||""} onChange={e=>setEditing({...editing, horaires:e.target.value})} className="ops-input" /></label>
              <label>Prix moyen FCFA<input type="number" value={editing.prix_moyen_fcfa??""} onChange={e=>setEditing({...editing, prix_moyen_fcfa: e.target.value===""?null:parseInt(e.target.value,10)})} className="ops-input" /></label>
              <label>Priorité (1-10)<input type="number" min="1" max="10" value={editing.priority??5} onChange={e=>setEditing({...editing, priority:parseInt(e.target.value,10)})} className="ops-input" /></label>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={editing.active!==false} onChange={e=>setEditing({...editing, active:e.target.checked})} /> Actif</label>
            </div>
            {fields.length > 0 && (
              <fieldset style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
                <legend>Champs custom</legend>
                <div className="ops-grid-2">
                  {fields.map(f => (
                    <label key={f.key}>{f.label}{f.optional?"":" *"}
                      <input
                        type={f.type === "url" ? "url" : f.type === "number" ? "number" : f.type === "phone" ? "tel" : "text"}
                        value={(editing.custom_fields||{})[f.key]||""}
                        onChange={e=>setEditing({...editing, custom_fields: {...(editing.custom_fields||{}), [f.key]: e.target.value }})}
                        className="ops-input"
                      />
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button type="button" className="ops-btn ops-btn-ghost" onClick={()=>setEditing(null)}>Annuler</button>
              <button type="submit" className="ops-btn ops-btn-primary" data-testid="recs-edit-save">💾 Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function RecsCategoriesPanel({ token }) {
  const [cats, setCats] = useState([]);
  const [newId, setNewId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newSub, setNewSub] = useState({}); // catId -> subname
  const reload = () => axios.get(`${API}/ops/sponsored/categories`, { headers: authHeaders(token) }).then(r => setCats(r.data.categories || []));
  useEffect(() => { reload(); }, [token]); // eslint-disable-line
  const addCat = async () => {
    if (!newId.trim()) return;
    try { await axios.post(`${API}/ops/sponsored/categories`, { id: newId, label: newLabel || newId }, { headers: authHeaders(token) }); setNewId(""); setNewLabel(""); reload(); toast.success("Catégorie ajoutée"); }
    catch(e){ toast.error(e.response?.data?.error || e.message); }
  };
  const addSub = async (cid) => {
    const v = (newSub[cid] || "").trim(); if (!v) return;
    await axios.post(`${API}/ops/sponsored/categories`, { id: cid, subcategory: v }, { headers: authHeaders(token) });
    setNewSub({...newSub, [cid]: ""}); reload();
  };
  const delCat = async (cid) => {
    if (!window.confirm(`Supprimer catégorie ${cid} ?`)) return;
    try { await axios.delete(`${API}/ops/sponsored/categories/${cid}`, { headers: authHeaders(token) }); reload(); }
    catch(e){ toast.error(e.response?.data?.error === 'category_in_use' ? "Catégorie utilisée par des entries actives" : e.message); }
  };
  return (
    <div data-testid="recs-categories-panel">
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input className="ops-input" placeholder="id (ex: services_pro)" value={newId} onChange={e=>setNewId(e.target.value)} data-testid="recs-newcat-id" />
        <input className="ops-input" placeholder="Label (Services pro)" value={newLabel} onChange={e=>setNewLabel(e.target.value)} />
        <button type="button" className="ops-btn ops-btn-primary" onClick={addCat} data-testid="recs-newcat-add">+ Catégorie</button>
      </div>
      {cats.map(c => (
        <div key={c.id} className="ops-card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>{c.label}</strong>
            <button type="button" className="ops-btn ops-btn-ghost" onClick={() => delCat(c.id)}>🗑️</button>
          </div>
          <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>id: {c.id}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
            {(c.subcategories||[]).map(s => <span key={s} style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 9999, fontSize: 11 }}>{s}</span>)}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <input className="ops-input" placeholder="+ sous-catégorie" value={newSub[c.id]||""} onChange={e=>setNewSub({...newSub, [c.id]: e.target.value})} />
            <button type="button" className="ops-btn ops-btn-ghost" onClick={()=>addSub(c.id)}>+ Sous-cat</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RecsSchemaPanel({ token }) {
  const [fields, setFields] = useState([]);
  const [nf, setNf] = useState({ key: "", label: "", type: "text", optional: true });
  const reload = () => axios.get(`${API}/ops/sponsored/schema/fields`, { headers: authHeaders(token) }).then(r => setFields(r.data.fields || []));
  useEffect(() => { reload(); }, [token]); // eslint-disable-line
  const add = async () => {
    if (!nf.key.trim()) return;
    try { await axios.post(`${API}/ops/sponsored/schema/fields`, nf, { headers: authHeaders(token) }); setNf({ key:"", label:"", type:"text", optional:true }); reload(); toast.success("Champ ajouté"); }
    catch(e){ toast.error(e.response?.data?.error || e.message); }
  };
  const remove = async (k) => {
    if (!window.confirm(`Retirer le champ "${k}" du schéma ?`)) return;
    await axios.delete(`${API}/ops/sponsored/schema/fields/${encodeURIComponent(k)}`, { headers: authHeaders(token) });
    reload();
  };
  return (
    <div data-testid="recs-schema-panel">
      <div className="ops-card" style={{ marginBottom: 12 }}>
        <h4>+ Nouveau champ custom</h4>
        <div className="ops-grid-2" style={{ gap: 8 }}>
          <label>key (slug)<input className="ops-input" value={nf.key} onChange={e=>setNf({...nf, key:e.target.value})} data-testid="recs-newfield-key" /></label>
          <label>Label<input className="ops-input" value={nf.label} onChange={e=>setNf({...nf, label:e.target.value})} /></label>
          <label>Type<select className="ops-input" value={nf.type} onChange={e=>setNf({...nf, type:e.target.value})}>
            <option value="text">text</option><option value="phone">phone</option><option value="url">url</option><option value="number">number</option>
          </select></label>
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={nf.optional} onChange={e=>setNf({...nf, optional:e.target.checked})} /> Optionnel</label>
        </div>
        <button type="button" className="ops-btn ops-btn-primary" onClick={add} style={{marginTop:8}} data-testid="recs-newfield-add">+ Ajouter</button>
      </div>
      <table className="ops-table">
        <thead><tr><th>key</th><th>Label</th><th>Type</th><th>Optional</th><th></th></tr></thead>
        <tbody>{fields.map(f => (
          <tr key={f.key}><td><code>{f.key}</code></td><td>{f.label}</td><td>{f.type}</td><td>{f.optional?"✓":""}</td>
          <td><button type="button" className="ops-btn ops-btn-ghost" onClick={()=>remove(f.key)}>🗑️</button></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function RecsConfigPanel({ token }) {
  const [cfg, setCfg] = useState(null);
  const [testQ, setTestQ] = useState("meilleur poulet braisé à Cocody");
  const [testRes, setTestRes] = useState(null);
  const reload = () => axios.get(`${API}/ops/sponsored/config`, { headers: authHeaders(token) }).then(r => setCfg(r.data.config));
  useEffect(() => { reload(); }, [token]); // eslint-disable-line
  if (!cfg) return <div>Chargement…</div>;
  const save = async () => {
    try { const r = await axios.put(`${API}/ops/sponsored/config`, cfg, { headers: authHeaders(token) }); setCfg(r.data.config); toast.success("Config sauvegardée"); }
    catch(e){ toast.error(e.response?.data?.error || e.message); }
  };
  const test = async () => {
    const r = await axios.post(`${API}/ops/test/sponsored-trigger`, { query: testQ }, { headers: authHeaders(token) });
    setTestRes(r.data);
  };
  return (
    <div data-testid="recs-config-panel">
      <div className="ops-card" style={{ marginBottom: 14 }}>
        <h4>⚙️ Configuration globale</h4>
        <fieldset style={{ border: 0, padding: 0, margin: "10px 0" }}>
          <legend><strong>Trigger mode</strong></legend>
          {[
            ["strict", "STRICT — exige superlatif (meilleur, top, recommande…)"],
            ["large",  "LARGE — déclenche dès qu'une catégorie est mentionnée"],
            ["smart",  "SMART (défaut) — catégorie + intention OU verbe d'action / commune"],
          ].map(([v, label]) => (
            <label key={v} style={{ display: "block", margin: "4px 0" }}>
              <input type="radio" name="trigger" value={v} checked={cfg.trigger_mode===v} onChange={()=>setCfg({...cfg, trigger_mode:v})} data-testid={`recs-trigger-${v}`} /> {label}
            </label>
          ))}
        </fieldset>
        <label style={{ display: "block", margin: "8px 0" }}><strong>Cartes affichées</strong>
          <select value={cfg.max_cards_displayed} onChange={e=>setCfg({...cfg, max_cards_displayed:parseInt(e.target.value,10)})} className="ops-input" data-testid="recs-maxcards">
            <option value={3}>3</option><option value={5}>5</option>
          </select>
        </label>
        <label style={{ display: "block", margin: "8px 0" }}><input type="checkbox" checked={cfg.show_disclosure} onChange={e=>setCfg({...cfg, show_disclosure:e.target.checked})} data-testid="recs-show-disclosure" /> <strong>Afficher la mention légale</strong></label>
        <label style={{ display: "block", margin: "8px 0" }}><strong>Texte de la mention</strong>
          <input className="ops-input" value={cfg.disclosure_text} onChange={e=>setCfg({...cfg, disclosure_text:e.target.value})} />
        </label>
        <button type="button" className="ops-btn ops-btn-primary" onClick={save} data-testid="recs-config-save">💾 Sauvegarder</button>
      </div>

      <div className="ops-card">
        <h4>🧪 Tester un trigger</h4>
        <div style={{ display:"flex", gap: 6, marginBottom: 8 }}>
          <input className="ops-input" value={testQ} onChange={e=>setTestQ(e.target.value)} data-testid="recs-trigger-query" />
          <button type="button" className="ops-btn ops-btn-primary" onClick={test} data-testid="recs-trigger-run">Tester</button>
        </div>
        {testRes && (
          <pre data-testid="recs-trigger-result" style={{ background:"#0f172a", color:"#e2e8f0", padding:10, borderRadius:8, fontSize:12, overflow:"auto", maxHeight: 320 }}>
{JSON.stringify(testRes, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// v1.14.0 — Voice Languages tab (Dioula / Sango master switches + tester)
// ===========================================================================
function VoiceLanguagesTab({ token }) {
  const [langs, setLangs] = useState(null);
  const [testQ, setTestQ] = useState("balaô tongana nyen");
  const [testRes, setTestRes] = useState(null);
  const reload = () => axios.get(`${API}/ops/modes/casual/voice-languages`, { headers: authHeaders(token) }).then(r => setLangs(r.data.voice_languages || {}));
  useEffect(() => { reload(); }, [token]); // eslint-disable-line
  if (!langs) return <div>Chargement…</div>;
  const save = async (patch) => {
    try { const r = await axios.put(`${API}/ops/modes/casual/voice-languages`, patch, { headers: authHeaders(token) }); setLangs(r.data.voice_languages); toast.success("Langues mises à jour"); }
    catch(e){ toast.error(e.response?.data?.error || e.message); }
  };
  const test = async () => {
    const r = await axios.post(`${API}/ops/test/lang-trigger`, { query: testQ }, { headers: authHeaders(token) });
    setTestRes(r.data);
  };
  return (
    <div className="ops-section" data-testid="ops-voice-languages">
      <h2>🌍 Langues vocales africaines</h2>
      <p className="ops-meta">Disponibles uniquement en mode vocal Live (mode casual). Le mode texte refuse toujours pour ces langues.</p>
      <div className="ops-card" style={{ marginBottom: 12 }} data-testid="lang-dioula">
        <h4>🇨🇮 Dioula / Bambara</h4>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={langs.dioula_oral_enabled !== false} onChange={e => save({ dioula_oral_enabled: e.target.checked })} data-testid="dioula-toggle" />
          <strong>{langs.dioula_oral_enabled !== false ? "Activé" : "Désactivé"}</strong>
        </label>
        <p className="ops-meta">Permet à l'IA de parler dioula à l'oral si l'utilisateur le demande. Mode texte refuse toujours.</p>
        <p className="ops-meta">Qualité prononciation : <em>Best-effort (Gemini native-audio)</em></p>
      </div>
      <div className="ops-card" style={{ marginBottom: 12 }} data-testid="lang-sango">
        <h4>🇨🇫 Sango (République Centrafricaine)</h4>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={!!langs.sango_oral_enabled} onChange={e => save({ sango_oral_enabled: e.target.checked })} data-testid="sango-toggle" />
          <strong>{langs.sango_oral_enabled ? "Activé" : "Désactivé"}</strong>
        </label>
        <p className="ops-meta">Permet à l'IA de parler sango (RCA) à l'oral. Activable seulement pour tests utilisateur. Le mode texte refuse toujours.</p>
        <p className="ops-meta">Qualité prononciation : <em>Best-effort — langue non supportée officiellement par Gemini Live, qualité audio variable, parfois accent français</em>.</p>
      </div>
      <div className="ops-card">
        <h4>🧪 Tester un trigger linguistique</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <input className="ops-input" value={testQ} onChange={e=>setTestQ(e.target.value)} data-testid="lang-trigger-query" />
          <button type="button" className="ops-btn ops-btn-primary" onClick={test} data-testid="lang-trigger-run">Tester</button>
        </div>
        {testRes && (
          <pre data-testid="lang-trigger-result" style={{ background: "#0f172a", color: "#e2e8f0", padding: 10, borderRadius: 8, fontSize: 12, marginTop: 8, overflow: "auto" }}>
{JSON.stringify(testRes, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RAG TAB — v1.15.0 (4 sections : Stats / Test / Browser CRUD / Avancé)
// ---------------------------------------------------------------------------

const RAG_ENTITY_TYPES = [
  { key: "banks_commercial",     label: "🏦 Banques commerciales" },
  { key: "banks_mobile_money",   label: "📱 Mobile Money" },
  { key: "ministeres",           label: "🏛️ Ministères" },
  { key: "universites",          label: "🎓 Universités" },
  { key: "hopitaux",             label: "🏥 Hôpitaux" },
  { key: "medias_television",    label: "📺 TV" },
  { key: "medias_radio",         label: "📻 Radio" },
  { key: "medias_presse_ecrite", label: "📰 Presse écrite" },
  { key: "medias_en_ligne",      label: "💻 Médias en ligne" },
  { key: "villes",               label: "📍 Villes" },
  { key: "marches",              label: "🛒 Marchés" },
  { key: "gastronomie",          label: "🍽️ Gastronomie" },
];

function RagTab({ token }) {
  const [section, setSection] = useState("stats");
  return (
    <div className="ops-section" data-testid="rag-tab">
      <h2>🧠 RAG Engine v2.0 — Base de connaissances structurée</h2>
      <p style={{ color: "#64748b", marginTop: -6, marginBottom: 14, fontSize: 13 }}>
        Moteur RAG <code>structured_v2</code>. Source :{" "}
        <code>/app/backend-node/data/civ_knowledge.json</code>. Toute mutation
        recharge automatiquement la base en mémoire et bump <code>_meta.derniere_maj</code>.
      </p>

      <div className="ops-tabs ops-subtabs" style={{ marginBottom: 16 }}>
        {[
          ["stats",   "📊 Stats"],
          ["test",    "🧪 Tester une query"],
          ["browser", "🗂️ Explorer / CRUD"],
          ["advanced","⚙️ Avancé"],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`ops-tab ${section === k ? "is-active" : ""}`}
            onClick={() => setSection(k)}
            data-testid={`rag-subtab-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {section === "stats" && <RagStatsSection token={token} />}
      {section === "test" && <RagTestSection token={token} />}
      {section === "browser" && <RagBrowserSection token={token} />}
      {section === "advanced" && <RagAdvancedSection token={token} />}
    </div>
  );
}

function RagStatsSection({ token }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/ops/rag/stats`, { headers: authHeaders(token) });
      setStats(r.data.stats);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (err) return <div style={{ color: "#dc2626" }} data-testid="rag-stats-err">Erreur: {err}</div>;
  if (!stats) return <div data-testid="rag-stats-loading">Chargement…</div>;

  return (
    <div data-testid="rag-stats-section">
      <fieldset className="ops-fieldset">
        <legend>📊 Compteurs base v{stats.version}</legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          {Object.entries(stats.counts || {}).map(([k, v]) => (
            <div key={k} style={{ background: "#f1f5f9", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} data-testid={`rag-count-${k}`}>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {k.replace(/_/g, " ")}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, fontSize: 12, color: "#475569" }}>
          Dernière mise à jour : <strong>{stats.derniere_maj}</strong>
        </div>
      </fieldset>

      <fieldset className="ops-fieldset">
        <legend>🎯 Intents disponibles ({(stats.intents || stats.intents_disponibles || []).length})</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(stats.intents || stats.intents_disponibles || []).map(i => (
            <span key={i} className="ops-pill" style={{ background: "#dbeafe", color: "#1e40af", padding: "4px 10px", borderRadius: 999, fontSize: 12 }} data-testid={`rag-intent-${i}`}>
              {i}
            </span>
          ))}
        </div>
      </fieldset>

      <button className="ops-btn" onClick={refresh} data-testid="rag-stats-refresh">🔄 Rafraîchir</button>
    </div>
  );
}

function RagTestSection({ token }) {
  const [query, setQuery] = useState("où ouvrir un compte bancaire à Cocody");
  const [voiceMode, setVoiceMode] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/ops/rag/test`,
        { query, voice: voiceMode },
        { headers: authHeaders(token) }
      );
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  // v1.16.0 — la nouvelle shape : result.result = { text, media }
  const ragResult = result?.result || null;
  const text = ragResult?.text || result?.context || null;
  const media = ragResult?.media || [];

  return (
    <div data-testid="rag-test-section">
      <fieldset className="ops-fieldset">
        <legend>🧪 Tester une query</legend>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          rows={3}
          className="ops-input"
          placeholder="Tape une question utilisateur..."
          data-testid="rag-test-input"
          style={{ width: "100%", fontFamily: "inherit" }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="ops-btn ops-btn-primary" onClick={run} disabled={loading} data-testid="rag-test-run">
            {loading ? "⏳ ..." : "🔍 Lancer"}
          </button>
          <button className="ops-btn" onClick={() => { setQuery(""); setResult(null); }} data-testid="rag-test-clear">
            Effacer
          </button>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={voiceMode}
              onChange={(e) => setVoiceMode(e.target.checked)}
              data-testid="rag-test-voice"
            />
            Mode voix (exclut médias)
          </label>
        </div>
        {result && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>
              Match :{" "}
              <strong style={{ color: result.matched ? "#059669" : "#dc2626" }} data-testid="rag-test-matched">
                {result.matched ? "✓ trouvé" : "✗ aucun intent"}
              </strong>
              {" · "}
              <span data-testid="rag-test-media-count">Médias : <strong>{media.length}</strong></span>
            </div>
            {media.length > 0 && (
              <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {media.map((m, i) => (
                  <span
                    key={i}
                    data-testid={`rag-test-media-chip-${i}`}
                    title={m.url}
                    style={{
                      background: m.type === "map" ? "#dbeafe" : m.type === "image" ? "#fce7f3" : "#dcfce7",
                      color: m.type === "map" ? "#1e40af" : m.type === "image" ? "#9d174d" : "#166534",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontFamily: "monospace",
                    }}
                  >
                    {m.type} · {(m.url || "").slice(0, 50)}{(m.url || "").length > 50 ? "…" : ""}
                  </span>
                ))}
              </div>
            )}
            <pre data-testid="rag-test-result" style={{ background: "#0f172a", color: "#e2e8f0", padding: 12, borderRadius: 8, fontSize: 12, overflow: "auto", whiteSpace: "pre-wrap" }}>
{text || "(null — pas de contexte généré)"}
            </pre>
          </div>
        )}
      </fieldset>
    </div>
  );
}

function RagBrowserSection({ token }) {
  const [type, setType] = useState("banks_commercial");
  const [entities, setEntities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (t = type) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/ops/rag/entities/${t}`, { headers: authHeaders(token) });
      setEntities(r.data.entities || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(type); /* eslint-disable-next-line */ }, [type]);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.nom) {
      toast.error("Le champ `nom` est requis");
      return;
    }
    setBusy(true);
    try {
      if (editing._is_new) {
        const body = { ...editing };
        delete body._is_new;
        await axios.post(`${API}/ops/rag/entities/${type}`, body, { headers: authHeaders(token) });
        toast.success("Entité créée");
      } else {
        const body = { ...editing };
        delete body._is_new;
        await axios.put(`${API}/ops/rag/entities/${type}/${editing.id}`, body, { headers: authHeaders(token) });
        toast.success("Entité mise à jour");
      }
      setEditing(null);
      load(type);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Supprimer ${id} ?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/ops/rag/entities/${type}/${id}`, { headers: authHeaders(token) });
      toast.success("Supprimé");
      load(type);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="rag-browser-section">
      <fieldset className="ops-fieldset">
        <legend>🗂️ Explorer & CRUD</legend>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13 }}>Type :</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="ops-input"
            data-testid="rag-browser-type"
          >
            {RAG_ENTITY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button
            className="ops-btn ops-btn-primary"
            onClick={() => setEditing({ _is_new: true, id: "", nom: "" })}
            data-testid="rag-browser-add"
          >
            ➕ Ajouter
          </button>
          <button className="ops-btn" onClick={() => load(type)} data-testid="rag-browser-refresh">🔄</button>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {entities.length} entité(s){busy ? " · ..." : ""}
          </span>
        </div>
        {err && <div style={{ color: "#dc2626", marginTop: 8 }}>Erreur: {err}</div>}

        <div style={{ marginTop: 12, maxHeight: 480, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }} data-testid="rag-browser-table">
            <thead style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
              <tr>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>id</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>nom</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>commune / type</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>tél</th>
                <th style={{ padding: 8, borderBottom: "1px solid #e2e8f0" }}></th>
              </tr>
            </thead>
            <tbody>
              {entities.map(e => (
                <tr key={e.id} style={{ borderBottom: "1px solid #f1f5f9" }} data-testid={`rag-row-${e.id}`}>
                  <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>{e.id}</td>
                  <td style={{ padding: 8, fontWeight: 600 }}>{e.nom}</td>
                  <td style={{ padding: 8, color: "#475569" }}>{e.commune || e.type || ""}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>{e.telephone || ""}</td>
                  <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                    <button className="ops-btn ops-btn-sm" onClick={() => setEditing({ ...e })} data-testid={`rag-edit-${e.id}`}>✏️</button>
                    <button className="ops-btn ops-btn-sm" onClick={() => onDelete(e.id)} style={{ marginLeft: 4, color: "#dc2626" }} data-testid={`rag-del-${e.id}`}>🗑️</button>
                  </td>
                </tr>
              ))}
              {!entities.length && (
                <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>Aucune entité</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </fieldset>

      {editing && (
        <fieldset className="ops-fieldset" data-testid="rag-edit-form">
          <legend>{editing._is_new ? "➕ Nouvelle entité" : `✏️ Édition — ${editing.id}`}</legend>
          <RagEntityEditor entity={editing} onChange={setEditing} disabled={busy} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="ops-btn ops-btn-primary" onClick={onSave} disabled={busy} data-testid="rag-edit-save">
              💾 Sauvegarder
            </button>
            <button className="ops-btn" onClick={() => setEditing(null)} disabled={busy} data-testid="rag-edit-cancel">
              Annuler
            </button>
          </div>
        </fieldset>
      )}
    </div>
  );
}

function RagEntityEditor({ entity, onChange, disabled }) {
  const [jsonStr, setJsonStr] = useState(() => JSON.stringify(entity, null, 2));
  const [jsonErr, setJsonErr] = useState(null);

  useEffect(() => {
    setJsonStr(JSON.stringify(entity, null, 2));
    setJsonErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id, entity?._is_new]);

  const onTextChange = (v) => {
    setJsonStr(v);
    try {
      const parsed = JSON.parse(v);
      setJsonErr(null);
      onChange(parsed);
    } catch (e) {
      setJsonErr(e.message);
    }
  };

  const set = (k, v) => {
    const next = { ...entity, [k]: v };
    onChange(next);
    setJsonStr(JSON.stringify(next, null, 2));
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          id {entity._is_new && <em style={{ fontSize: 11, color: "#94a3b8" }}>(auto si vide)</em>}
          <input className="ops-input" value={entity.id || ""} onChange={(e) => set("id", e.target.value)} disabled={disabled || !entity._is_new} data-testid="rag-field-id" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          nom *
          <input className="ops-input" value={entity.nom || ""} onChange={(e) => set("nom", e.target.value)} disabled={disabled} data-testid="rag-field-nom" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          adresse
          <input className="ops-input" value={entity.adresse || ""} onChange={(e) => set("adresse", e.target.value)} disabled={disabled} data-testid="rag-field-adresse" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          téléphone
          <input className="ops-input" value={entity.telephone || ""} onChange={(e) => set("telephone", e.target.value)} disabled={disabled} data-testid="rag-field-telephone" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          commune
          <input className="ops-input" value={entity.commune || ""} onChange={(e) => set("commune", e.target.value)} disabled={disabled} data-testid="rag-field-commune" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          site_web
          <input className="ops-input" value={entity.site_web || ""} onChange={(e) => set("site_web", e.target.value)} disabled={disabled} data-testid="rag-field-site" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          image_url
          <input className="ops-input" value={entity.image_url || ""} onChange={(e) => set("image_url", e.target.value)} disabled={disabled} data-testid="rag-field-image-url" placeholder="https://..." />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          photo_url
          <input className="ops-input" value={entity.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} disabled={disabled} data-testid="rag-field-photo-url" placeholder="https://..." />
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          coordonnees.lat
          <input
            className="ops-input"
            type="number"
            step="0.0001"
            value={entity.coordonnees?.lat ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? null : parseFloat(e.target.value);
              const nextCoords = { ...(entity.coordonnees || {}), lat: v };
              set("coordonnees", nextCoords);
            }}
            disabled={disabled}
            data-testid="rag-field-lat"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          coordonnees.lng
          <input
            className="ops-input"
            type="number"
            step="0.0001"
            value={entity.coordonnees?.lng ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? null : parseFloat(e.target.value);
              const nextCoords = { ...(entity.coordonnees || {}), lng: v };
              set("coordonnees", nextCoords);
            }}
            disabled={disabled}
            data-testid="rag-field-lng"
          />
        </label>
      </div>
      <details>
        <summary style={{ cursor: "pointer", fontSize: 12, color: "#475569" }}>📝 Édition JSON brute (avancée)</summary>
        <textarea
          value={jsonStr}
          onChange={(e) => onTextChange(e.target.value)}
          rows={12}
          className="ops-input"
          disabled={disabled}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, marginTop: 6 }}
          data-testid="rag-field-json"
        />
        {jsonErr && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>JSON invalide: {jsonErr}</div>}
      </details>
    </div>
  );
}

function RagAdvancedSection({ token }) {
  const [busy, setBusy] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importMsg, setImportMsg] = useState(null);

  const reload = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/ops/rag/reload`, {}, { headers: authHeaders(token) });
      toast.success("Base rechargée");
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const exportDb = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/ops/rag/export`, { headers: authHeaders(token) });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `civ_knowledge_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const importDb = async () => {
    setBusy(true);
    setImportMsg(null);
    try {
      let parsed;
      try { parsed = JSON.parse(importJson); } catch (e) {
        setImportMsg({ kind: "err", text: "JSON invalide: " + e.message });
        return;
      }
      const r = await axios.post(`${API}/ops/rag/import`, { db: parsed }, { headers: authHeaders(token) });
      setImportMsg({ kind: "ok", text: `Import OK — ${r.data.validation?.total_entities} entités` });
      toast.success("Base importée");
    } catch (e) {
      setImportMsg({ kind: "err", text: e.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="rag-advanced-section">
      <fieldset className="ops-fieldset">
        <legend>⚙️ Opérations avancées</legend>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="ops-btn" onClick={reload} disabled={busy} data-testid="rag-adv-reload">
            🔄 Reload base mémoire
          </button>
          <button className="ops-btn" onClick={exportDb} disabled={busy} data-testid="rag-adv-export">
            📤 Exporter JSON
          </button>
        </div>
      </fieldset>

      <fieldset className="ops-fieldset">
        <legend>📥 Importer un JSON complet (validation schema_version="structured_v2")</legend>
        <textarea
          value={importJson}
          onChange={(e) => setImportJson(e.target.value)}
          rows={10}
          className="ops-input"
          placeholder='{ "_meta": { "schema_version": "structured_v2", ... }, "banks": ... }'
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
          data-testid="rag-adv-import-input"
        />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="ops-btn ops-btn-primary" onClick={importDb} disabled={busy || !importJson.trim()} data-testid="rag-adv-import-run">
            ⚠️ Remplacer la base
          </button>
        </div>
        {importMsg && (
          <div
            data-testid="rag-adv-import-msg"
            style={{
              marginTop: 8,
              padding: 8,
              borderRadius: 6,
              background: importMsg.kind === "ok" ? "#dcfce7" : "#fee2e2",
              color: importMsg.kind === "ok" ? "#166534" : "#991b1b",
              fontSize: 13,
            }}
          >
            {importMsg.text}
          </div>
        )}
      </fieldset>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// v1.19.0 — IntentsTab : édition data-driven des intents RAG
// ════════════════════════════════════════════════════════════════
function IntentsTab({ token }) {
  const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  const [config, setConfig] = useState({ intents: [], _meta: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createDraft, setCreateDraft] = useState(null);
  const [testModal, setTestModal] = useState(null);
  const [codeHandlers, setCodeHandlers] = useState([]);

  const fetchAll = async () => {
    setLoading(true); setError(null);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/ops/rag/intents`, { headers: { "x-admin-token": token } }),
        fetch(`${API}/ops/rag/intents/code-handlers`, { headers: { "x-admin-token": token } }),
      ]);
      const d1 = await r1.json(); const d2 = await r2.json();
      if (!d1.ok) throw new Error(d1.error || "fetch failed");
      setConfig({ intents: d1.intents, _meta: d1._meta });
      setCodeHandlers(d2.handlers || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const toggleEnabled = async (intent, enabled) => {
    const r = await fetch(`${API}/ops/rag/intents/${intent.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify({ enabled }),
    });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${(d.errors || [d.error]).join(', ')}`); return; }
    fetchAll();
  };

  const startEdit = (intent) => {
    setExpandedId(intent.id);
    setEditDraft(JSON.parse(JSON.stringify(intent)));
  };
  const cancelEdit = () => { setExpandedId(null); setEditDraft(null); };

  const saveEdit = async () => {
    if (!editDraft) return;
    const r = await fetch(`${API}/ops/rag/intents/${editDraft.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify(editDraft),
    });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${(d.errors || [d.error]).join(', ')}`); return; }
    cancelEdit(); fetchAll();
  };

  const deleteIntent = async (intent) => {
    if (!window.confirm(`Supprimer l'intent "${intent.name}" ?`)) return;
    const r = await fetch(`${API}/ops/rag/intents/${intent.id}`, {
      method: "DELETE", headers: { "x-admin-token": token },
    });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${d.error}`); return; }
    fetchAll();
  };

  const reloadConfig = async () => {
    await fetch(`${API}/ops/rag/intents/reload`, { method: "POST", headers: { "x-admin-token": token } });
    fetchAll();
  };

  const runTest = async (intentId, query) => {
    const r = await fetch(`${API}/ops/rag/intents/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify({ intent_id: intentId, query }),
    });
    return r.json();
  };

  const createIntent = async () => {
    if (!createDraft) return;
    const r = await fetch(`${API}/ops/rag/intents`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify(createDraft),
    });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${(d.errors || [d.error]).join(', ')}`); return; }
    setShowCreate(false); setCreateDraft(null); fetchAll();
  };

  const enabledCount = config.intents.filter((i) => i.enabled !== false).length;

  return (
    <div data-testid="intents-tab" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>🎯 Intents RAG — Configuration data-driven</h2>
          <div style={{ fontSize: 12, color: "var(--civ-text-muted)", marginTop: 4 }}>
            <span style={{ display: "inline-block", padding: "2px 8px", background: "rgba(34,197,94,0.15)", color: "#15803d", borderRadius: 6, marginRight: 6 }}>
              ✓ Auto-reload actif (fs.watch + watchFile)
            </span>
            Fichier : <code>data/rag_intents.json</code> — v{config._meta?.version || "?"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{ padding: "6px 12px", background: "rgba(59,130,246,0.12)", borderRadius: 8, fontSize: 13 }} data-testid="intents-counter">
            {enabledCount} actifs / {config.intents.length} total
          </span>
          <button onClick={reloadConfig} className="ops-btn" data-testid="intents-reload-btn">↻ Reload</button>
          <button
            onClick={() => {
              setShowCreate(true);
              setCreateDraft({
                name: "", enabled: true, keywords: [], handler_type: "search_by_name",
                section_path: "", max_items: 3, title_emoji: "📌", title_label: "",
                item_template: "{nom}\n  Adresse : {adresse}\n  Tél : {telephone}",
              });
            }}
            data-testid="intents-new-btn"
            style={{ background: "#0d6efd", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}
          >
            + Nouvel intent
          </button>
        </div>
      </div>

      {error && <div style={{ padding: 10, background: "#fee", color: "#900", borderRadius: 6, marginBottom: 12 }}>Erreur : {error}</div>}
      {loading && <div style={{ fontSize: 13, color: "var(--civ-text-muted)" }}>Chargement…</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {config.intents.map((intent) => (
          <IntentCard
            key={intent.id}
            intent={intent}
            expanded={expandedId === intent.id}
            draft={expandedId === intent.id ? editDraft : null}
            setDraft={setEditDraft}
            codeHandlers={codeHandlers}
            onToggle={(en) => toggleEnabled(intent, en)}
            onStartEdit={() => startEdit(intent)}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onDelete={() => deleteIntent(intent)}
            onTest={() => setTestModal({ intent_id: intent.id, intent_name: intent.name, query: "", result: null })}
          />
        ))}
      </div>

      {showCreate && (
        <IntentModal
          title="+ Nouvel intent"
          draft={createDraft}
          setDraft={setCreateDraft}
          codeHandlers={codeHandlers}
          onSave={createIntent}
          onClose={() => { setShowCreate(false); setCreateDraft(null); }}
          isCreate
        />
      )}

      {testModal && (
        <TestIntentModal
          intentName={testModal.intent_name}
          query={testModal.query}
          result={testModal.result}
          onQueryChange={(q) => setTestModal({ ...testModal, query: q })}
          onRun={async () => {
            const r = await runTest(testModal.intent_id, testModal.query);
            setTestModal({ ...testModal, result: r });
          }}
          onClose={() => setTestModal(null)}
        />
      )}
    </div>
  );
}

function IntentCard({ intent, expanded, draft, setDraft, codeHandlers, onToggle, onStartEdit, onSave, onCancel, onDelete, onTest }) {
  const isCode = intent.handler_type === "code";
  const handlerColor = intent.handler_type === "code" ? "#6b7280" : "#16a34a";
  const handlerBg = intent.handler_type === "code" ? "rgba(107,114,128,0.12)" : "rgba(34,197,94,0.12)";
  const isSponsored = intent.is_sponsored === true && intent.sponsor?.billing_status === "active";
  return (
    <div
      data-testid={`intent-card-${intent.name}`}
      style={{
        border: "1px solid var(--civ-border)",
        borderRadius: 10,
        background: "var(--civ-bg-elev)",
        padding: 14,
        opacity: intent.enabled === false ? 0.55 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 20 }}>{intent.title_emoji || "📌"}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }} data-testid={`intent-name-${intent.name}`}>
            {intent.name} <span style={{ fontWeight: 400, fontSize: 12, color: "var(--civ-text-muted)" }}>· {intent.title_label}</span>
            {isSponsored && (
              <span className="civ-sponsor-badge" data-testid={`intent-sponsor-badge-${intent.name}`} title={intent.sponsor.partner_name} style={{ marginLeft: 8 }}>
                💎 Sponsorisé
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--civ-text-muted)", marginTop: 2 }}>
            {intent.keywords?.length || 0} mots-clés
            {intent.section_path && ` · path: ${intent.section_path}`}
            {intent.section_paths && ` · paths: ${intent.section_paths.length}`}
            {isSponsored && ` · partenaire: ${intent.sponsor.partner_name}`}
          </div>
        </div>
        <span style={{ padding: "3px 10px", background: handlerBg, color: handlerColor, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
          {intent.handler_type}
        </span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }} data-testid={`intent-toggle-${intent.name}`}>
          <input type="checkbox" checked={intent.enabled !== false} onChange={(e) => onToggle(e.target.checked)} />
          {intent.enabled !== false ? "Actif" : "Désactivé"}
        </label>
        <button onClick={onTest} style={{ padding: "5px 10px", border: "1px solid var(--civ-border)", borderRadius: 6, background: "transparent", cursor: "pointer", fontSize: 12 }} data-testid={`intent-test-${intent.name}`}>🧪</button>
        {!expanded && <button onClick={onStartEdit} style={{ padding: "5px 10px", border: "1px solid var(--civ-border)", borderRadius: 6, background: "transparent", cursor: "pointer", fontSize: 12 }} data-testid={`intent-edit-${intent.name}`}>✏️ Éditer</button>}
      </div>

      {expanded && draft && (
        <div style={{ marginTop: 14, padding: 14, borderTop: "1px solid var(--civ-border)" }}>
          <Field label="Mots-clés (Entrée pour valider chaque tag)">
            <TagInput
              tags={draft.keywords || []}
              onChange={(v) => setDraft({ ...draft, keywords: v })}
              placeholder="ajouter…"
              testId={`intent-edit-keywords-${intent.name}`}
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Title emoji">
              <input value={draft.title_emoji || ""} onChange={(e) => setDraft({ ...draft, title_emoji: e.target.value })} style={inputStyleIntent} />
            </Field>
            <Field label="Title label">
              <input value={draft.title_label || ""} onChange={(e) => setDraft({ ...draft, title_label: e.target.value })} style={inputStyleIntent} />
            </Field>
          </div>
          {!isCode && (
            <>
              {draft.handler_type === "search_by_name" && (
                <Field label="Section path (dot-notation)">
                  <input value={draft.section_path || ""} onChange={(e) => setDraft({ ...draft, section_path: e.target.value })} style={inputStyleIntent} />
                </Field>
              )}
              {draft.handler_type === "search_by_name_multi" && (
                <Field label="Section paths (1 par ligne)">
                  <textarea
                    value={(draft.section_paths || []).join("\n")}
                    onChange={(e) => setDraft({ ...draft, section_paths: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    rows={3} style={{ ...inputStyleIntent, fontFamily: "monospace" }}
                  />
                </Field>
              )}
              <Field label="Max items">
                <input type="number" min={1} max={20} value={draft.max_items || 3} onChange={(e) => setDraft({ ...draft, max_items: parseInt(e.target.value, 10) })} style={inputStyleIntent} />
              </Field>
              <Field label={'Item template (utilise {champ})'}>
                <textarea
                  value={draft.item_template || ""}
                  onChange={(e) => setDraft({ ...draft, item_template: e.target.value })}
                  rows={5}
                  style={{ ...inputStyleIntent, fontFamily: "monospace", fontSize: 12 }}
                />
              </Field>
            </>
          )}
          {isCode && (
            <Field label="Code handler">
              <input value={draft.code_handler || ""} disabled style={{ ...inputStyleIntent, opacity: 0.6 }} />
              <div style={{ fontSize: 11, color: "var(--civ-text-muted)", marginTop: 4 }}>
                Handlers : {codeHandlers.join(", ")}. Code dans <code>ragEngine.js</code> ; UI édite uniquement keywords/title/enabled.
              </div>
            </Field>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={onSave} style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }} data-testid={`intent-save-${intent.name}`}>💾 Sauvegarder</button>
            <button onClick={onCancel} style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--civ-border)", borderRadius: 6, cursor: "pointer" }}>Annuler</button>
            <button onClick={onDelete} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #dc2626", color: "#dc2626", borderRadius: 6, cursor: "pointer", marginLeft: "auto" }} data-testid={`intent-delete-${intent.name}`}>🗑️ Supprimer</button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyleIntent = { width: "100%", padding: 8, border: "1px solid var(--civ-border)", borderRadius: 6, fontSize: 13 };

function Field({ label, children }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--civ-text-muted)", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function IntentModal({ title, draft, setDraft, codeHandlers, onSave, onClose, isCreate }) {
  if (!draft) return null;
  const isCode = draft.handler_type === "code";
  return (
    <div style={modalBackdropStyleIntent} onClick={onClose} data-testid="intent-modal">
      <div style={modalStyleIntent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <Field label="Nom (slug, lowercase)">
          <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={inputStyleIntent} data-testid="intent-create-name" />
        </Field>
        <Field label="Handler type">
          <select value={draft.handler_type} onChange={(e) => setDraft({ ...draft, handler_type: e.target.value })} style={inputStyleIntent} data-testid="intent-create-handler-type">
            <option value="search_by_name">search_by_name (1 section)</option>
            <option value="search_by_name_multi">search_by_name_multi (N sections)</option>
            <option value="code">code (handler dans ragEngine.js)</option>
          </select>
        </Field>
        <Field label="Mots-clés (Entrée pour valider chaque tag)">
          <TagInput
            tags={draft.keywords || []}
            onChange={(v) => setDraft({ ...draft, keywords: v })}
            placeholder="ex: festival, femua, masa"
            testId="intent-create-keywords"
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Title emoji"><input value={draft.title_emoji || ""} onChange={(e) => setDraft({ ...draft, title_emoji: e.target.value })} style={inputStyleIntent} /></Field>
          <Field label="Title label"><input value={draft.title_label || ""} onChange={(e) => setDraft({ ...draft, title_label: e.target.value })} style={inputStyleIntent} /></Field>
        </div>
        {draft.handler_type === "search_by_name" && (
          <Field label="Section path"><input value={draft.section_path || ""} onChange={(e) => setDraft({ ...draft, section_path: e.target.value })} style={inputStyleIntent} placeholder="ex: festivals ou banks.commercial" /></Field>
        )}
        {draft.handler_type === "search_by_name_multi" && (
          <Field label="Section paths (1 par ligne)">
            <textarea
              value={(draft.section_paths || []).join("\n")}
              onChange={(e) => setDraft({ ...draft, section_paths: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={3} style={{ ...inputStyleIntent, fontFamily: "monospace" }}
            />
          </Field>
        )}
        {isCode && (
          <Field label="Code handler">
            <select value={draft.code_handler || ""} onChange={(e) => setDraft({ ...draft, code_handler: e.target.value })} style={inputStyleIntent}>
              <option value="">— choisir —</option>
              {codeHandlers.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </Field>
        )}
        {!isCode && (
          <>
            <Field label="Max items"><input type="number" min={1} max={20} value={draft.max_items || 3} onChange={(e) => setDraft({ ...draft, max_items: parseInt(e.target.value, 10) })} style={inputStyleIntent} /></Field>
            <Field label={'Item template (utilise {champ})'}>
              <textarea value={draft.item_template || ""} onChange={(e) => setDraft({ ...draft, item_template: e.target.value })} rows={5} style={{ ...inputStyleIntent, fontFamily: "monospace", fontSize: 12 }} />
            </Field>
          </>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--civ-border)", borderRadius: 6, cursor: "pointer" }}>Annuler</button>
          <button onClick={onSave} style={{ padding: "8px 16px", background: "#0d6efd", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }} data-testid="intent-create-save">{isCreate ? "Créer" : "Sauvegarder"}</button>
        </div>
      </div>
    </div>
  );
}

function TestIntentModal({ intentName, query, result, onQueryChange, onRun, onClose }) {
  return (
    <div style={modalBackdropStyleIntent} onClick={onClose} data-testid="intent-test-modal">
      <div style={modalStyleIntent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>🧪 Tester l'intent "{intentName}"</h3>
        <Field label="Query de test">
          <textarea
            value={query} onChange={(e) => onQueryChange(e.target.value)}
            rows={3} style={{ ...inputStyleIntent, fontFamily: "monospace" }}
            placeholder="Ex: où ouvrir un compte bancaire à Cocody"
            data-testid="intent-test-query-input"
          />
        </Field>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button onClick={onRun} style={{ padding: "8px 16px", background: "#0d6efd", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }} data-testid="intent-test-run">🚀 Lancer le test</button>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--civ-border)", borderRadius: 6, cursor: "pointer" }}>Fermer</button>
        </div>
        {result && (
          <div style={{ marginTop: 14 }} data-testid="intent-test-result">
            <div style={{ display: "flex", gap: 12, fontSize: 13, marginBottom: 8 }}>
              <span style={{ padding: "3px 10px", background: result.matched ? "rgba(34,197,94,0.15)" : "rgba(220,38,38,0.15)", color: result.matched ? "#15803d" : "#991b1b", borderRadius: 4 }}>
                matched: {String(result.matched)}
              </span>
              <span>entities: {result.entities?.length || 0}</span>
              <span>media: {result.media?.length || 0}</span>
            </div>
            <pre style={{ padding: 12, background: "#0f172a", color: "#e2e8f0", borderRadius: 6, fontSize: 12, maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap" }}>
              {result.text || "(pas de texte rendu)"}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

const modalBackdropStyleIntent = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
};
const modalStyleIntent = {
  background: "var(--civ-bg-elev, #fff)",
  color: "var(--civ-text)",
  borderRadius: 12, padding: 22,
  maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
};


// ════════════════════════════════════════════════════════════════
// v1.20.0 — TagInput générique (Entrée valide, Backspace retire)
// ════════════════════════════════════════════════════════════════
function TagInput({ tags = [], onChange, placeholder = "Ajouter…", variant = "default", testId = "tag-input" }) {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);
  const chipClass = variant === "positif" ? "civ-tag-chip civ-tag-chip-positif"
    : variant === "negatif" ? "civ-tag-chip civ-tag-chip-negatif"
    : "civ-tag-chip";
  const handleKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const v = value.trim();
      if (v) {
        if (!tags.includes(v)) onChange([...tags, v]);
        setValue("");
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        const form = e.target.closest("form, [data-tab-scope]");
        const next = form?.querySelector("[data-tab-next]");
        if (next && next.focus) next.focus();
      }
    } else if (e.key === "Backspace" && value === "" && tags.length > 0) {
      onChange(tags.slice(0, -1));
    } else if (e.key === ",") {
      e.preventDefault();
      const v = value.trim();
      if (v && !tags.includes(v)) onChange([...tags, v]);
      setValue("");
    }
  };
  return (
    <div className="civ-tag-input-wrap" onClick={() => inputRef.current?.focus()} data-testid={testId}>
      {tags.map((t, i) => (
        <span key={i} className={chipClass}>
          {t}
          <button type="button" className="civ-tag-chip-remove" onClick={(e) => { e.stopPropagation(); onChange(tags.filter((_, idx) => idx !== i)); }} aria-label={`Retirer ${t}`}>×</button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="civ-tag-input-field"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tags.length === 0 ? placeholder : ""}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// v1.20.0 — PersonalitiesTab
// ════════════════════════════════════════════════════════════════
function PersonalitiesTab({ token }) {
  const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({});
  const [filter, setFilter] = useState({ q: "", categorie: "", enabledOnly: false });
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState(null);
  const [testQuery, setTestQuery] = useState("");
  const [testResult, setTestResult] = useState(null);

  const fetchAll = async () => {
    const r = await fetch(`${API}/ops/personalities`, { headers: { "x-admin-token": token } });
    const d = await r.json();
    setList(d.personalities || []);
    setMeta(d._meta || {});
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const startCreate = () => {
    setDraft({ nom: "", nom_variations: [], role: "", pays: "CI", annee_naissance: "", annee_fin: "", parti: "", ethnie: "", surnom: "", formation: "", photo_url: "", stats: [], points_positifs: [], points_negatifs: [], categorie: "politique", enabled: true });
    setShowModal(true);
  };
  const startEdit = (p) => { setDraft(JSON.parse(JSON.stringify(p))); setShowModal(true); };

  const save = async () => {
    if (!draft) return;
    const isEdit = !!draft.id;
    const url = isEdit ? `${API}/ops/personalities/${draft.id}` : `${API}/ops/personalities`;
    const method = isEdit ? "PUT" : "POST";
    const r = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify(draft),
    });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${(d.errors || [d.error]).join(', ')}`); return; }
    setShowModal(false); setDraft(null); fetchAll();
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.nom} ?`)) return;
    const r = await fetch(`${API}/ops/personalities/${p.id}`, { method: "DELETE", headers: { "x-admin-token": token } });
    const d = await r.json();
    if (!d.ok) { alert(`Erreur: ${d.error}`); return; }
    fetchAll();
  };

  const toggleEnabled = async (p, enabled) => {
    await fetch(`${API}/ops/personalities/${p.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify({ enabled }),
    });
    fetchAll();
  };

  const runChatTest = async (nom) => {
    setTestResult({ loading: true });
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const r = await fetch(`${base}/api/agent`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: `Qui est ${nom} ?`, mode: "casual", userId: "ops-test-personality", voice: false }),
    });
    const d = await r.json();
    setTestResult({ loading: false, text: d.text || "(pas de texte)" });
  };

  const filtered = list.filter((p) => {
    if (filter.enabledOnly && p.enabled === false) return false;
    if (filter.categorie && p.categorie !== filter.categorie) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      const cands = [p.nom, ...(p.nom_variations || [])].map((s) => (s || "").toLowerCase());
      if (!cands.some((c) => c.includes(q))) return false;
    }
    return true;
  });

  const activeCount = list.filter((p) => p.enabled !== false).length;

  return (
    <div data-testid="personalities-tab" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>👤 Personnalités — Base éditable</h2>
          <div style={{ fontSize: 12, color: "var(--civ-text-muted)", marginTop: 4 }}>
            <span style={{ padding: "2px 8px", background: "rgba(34,197,94,0.15)", color: "#15803d", borderRadius: 6, marginRight: 6 }}>
              ✓ Auto-card injection dans chat
            </span>
            v{meta.version || "?"} · {activeCount}/{list.length} actifs
          </div>
        </div>
        <button onClick={startCreate} data-testid="personalities-new-btn" style={{ background: "#0d6efd", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}>+ Nouvelle personnalité</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <input type="text" placeholder="Rechercher (nom ou variation)…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} style={{ ...inputStyleIntent, flex: 1, minWidth: 200 }} data-testid="personalities-filter-q" />
        <select value={filter.categorie} onChange={(e) => setFilter({ ...filter, categorie: e.target.value })} style={{ ...inputStyleIntent, width: 180 }} data-testid="personalities-filter-cat">
          <option value="">Toutes catégories</option>
          <option value="politique">Politique</option><option value="culture">Culture</option><option value="histoire">Histoire</option><option value="sport">Sport</option><option value="autre">Autre</option>
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--civ-text)" }}>
          <input type="checkbox" checked={filter.enabledOnly} onChange={(e) => setFilter({ ...filter, enabledOnly: e.target.checked })} /> Actifs uniquement
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
        {filtered.map((p) => (
          <div key={p.id} data-testid={`ops-personality-card-${p.id}`} style={{ border: "1px solid var(--civ-border)", borderRadius: 10, padding: 14, background: "var(--civ-bg-elev)", opacity: p.enabled === false ? 0.55 : 1 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
              {p.photo_url ? <img src={p.photo_url} alt={p.nom} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }} loading="lazy" />
                : <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#2a3550,#4a5a80)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{p.nom.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nom}</div>
                <div style={{ fontSize: 11, color: "var(--civ-text-muted)" }}>{p.role}</div>
              </div>
              <span className={`civ-pc-cat civ-pc-cat-${p.categorie}`} style={{ fontSize: 10, padding: "2px 6px" }}>{p.categorie}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--civ-text-muted)", marginBottom: 8 }}>
              {(p.nom_variations || []).length} variations · {(p.stats || []).length} stats · {(p.points_positifs || []).length}/{(p.points_negatifs || []).length} ±
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                <input type="checkbox" checked={p.enabled !== false} onChange={(e) => toggleEnabled(p, e.target.checked)} /> Actif
              </label>
              <button onClick={() => { setTestQuery(p.nom); runChatTest(p.nom); }} style={{ padding: "4px 8px", border: "1px solid var(--civ-border)", borderRadius: 5, background: "transparent", cursor: "pointer", fontSize: 11 }} data-testid={`personality-test-${p.id}`}>🧪</button>
              <button onClick={() => startEdit(p)} style={{ padding: "4px 8px", border: "1px solid var(--civ-border)", borderRadius: 5, background: "transparent", cursor: "pointer", fontSize: 11 }} data-testid={`personality-edit-${p.id}`}>✏️</button>
              <button onClick={() => remove(p)} style={{ padding: "4px 8px", border: "1px solid #dc2626", color: "#dc2626", borderRadius: 5, background: "transparent", cursor: "pointer", fontSize: 11 }}>🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && draft && <PersonalityModal draft={draft} setDraft={setDraft} onSave={save} onClose={() => { setShowModal(false); setDraft(null); }} />}

      {testResult && (
        <div style={modalBackdropStyleIntent} onClick={() => setTestResult(null)} data-testid="personality-test-modal">
          <div style={modalStyleIntent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>🧪 Test chat : "{testQuery}"</h3>
            {testResult.loading ? <div>Chargement…</div> : (
              <pre style={{ padding: 12, background: "#0f172a", color: "#e2e8f0", borderRadius: 6, fontSize: 12, maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap" }}>{testResult.text}</pre>
            )}
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button onClick={() => setTestResult(null)} style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--civ-border)", borderRadius: 6, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonalityModal({ draft, setDraft, onSave, onClose }) {
  return (
    <div style={modalBackdropStyleIntent} onClick={onClose} data-testid="personality-modal">
      <div style={{ ...modalStyleIntent, maxWidth: 640 }} onClick={(e) => e.stopPropagation()} data-tab-scope>
        <h3 style={{ marginTop: 0 }}>{draft.id ? `✏️ Éditer : ${draft.nom}` : "+ Nouvelle personnalité"}</h3>
        <Field label="Nom *">
          <input value={draft.nom || ""} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} style={inputStyleIntent} data-testid="personality-name-input" data-tab-next />
        </Field>
        <Field label="Variations de nom (Entrée pour valider)">
          <TagInput tags={draft.nom_variations || []} onChange={(v) => setDraft({ ...draft, nom_variations: v })} placeholder="ex: ADO" testId="personality-variations" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Rôle"><input value={draft.role || ""} onChange={(e) => setDraft({ ...draft, role: e.target.value })} style={inputStyleIntent} /></Field>
          <Field label="Catégorie">
            <select value={draft.categorie || "politique"} onChange={(e) => setDraft({ ...draft, categorie: e.target.value })} style={inputStyleIntent}>
              <option value="politique">Politique</option><option value="culture">Culture</option><option value="histoire">Histoire</option><option value="sport">Sport</option><option value="autre">Autre</option>
            </select>
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Field label="Pays"><input value={draft.pays || ""} onChange={(e) => setDraft({ ...draft, pays: e.target.value })} style={inputStyleIntent} /></Field>
          <Field label="Né"><input value={draft.annee_naissance || ""} onChange={(e) => setDraft({ ...draft, annee_naissance: e.target.value })} style={inputStyleIntent} /></Field>
          <Field label="Fin"><input value={draft.annee_fin || ""} onChange={(e) => setDraft({ ...draft, annee_fin: e.target.value })} style={inputStyleIntent} /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Parti / Domaine"><input value={draft.parti || ""} onChange={(e) => setDraft({ ...draft, parti: e.target.value })} style={inputStyleIntent} /></Field>
          <Field label="Ethnie"><input value={draft.ethnie || ""} onChange={(e) => setDraft({ ...draft, ethnie: e.target.value })} style={inputStyleIntent} /></Field>
        </div>
        <Field label="Surnom"><input value={draft.surnom || ""} onChange={(e) => setDraft({ ...draft, surnom: e.target.value })} style={inputStyleIntent} /></Field>
        <Field label="Formation"><input value={draft.formation || ""} onChange={(e) => setDraft({ ...draft, formation: e.target.value })} style={inputStyleIntent} /></Field>
        <Field label="Photo URL (optionnel)"><input value={draft.photo_url || ""} onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })} style={inputStyleIntent} placeholder="https://… ou /assets/personalities/xxx.jpg" /></Field>
        <Field label="Points positifs (max 5, Entrée pour valider)">
          <TagInput tags={draft.points_positifs || []} onChange={(v) => setDraft({ ...draft, points_positifs: v.slice(0, 5) })} variant="positif" placeholder="ex: Indépendance CI 1960" testId="personality-positifs" />
        </Field>
        <Field label="Points controversés (max 5)">
          <TagInput tags={draft.points_negatifs || []} onChange={(v) => setDraft({ ...draft, points_negatifs: v.slice(0, 5) })} variant="negatif" placeholder="ex: Parti unique" testId="personality-negatifs" />
        </Field>
        <Field label="Stats (max 3 — label:valeur par ligne)">
          <textarea
            value={(draft.stats || []).map((s) => `${s.label}:${s.value}`).join("\n")}
            onChange={(e) => setDraft({ ...draft, stats: e.target.value.split("\n").filter(Boolean).slice(0, 3).map((line) => { const [l, ...rest] = line.split(":"); return { label: (l || "").trim(), value: rest.join(":").trim() }; }) })}
            rows={3}
            style={{ ...inputStyleIntent, fontFamily: "monospace", fontSize: 12 }}
            placeholder="Mandats:3×&#10;Âge:83"
          />
        </Field>
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--civ-border)", borderRadius: 6, cursor: "pointer" }}>Annuler</button>
          <button onClick={onSave} style={{ padding: "8px 16px", background: "#0d6efd", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }} data-testid="personality-save">{draft.id ? "Sauvegarder" : "Créer"}</button>
        </div>
      </div>
    </div>
  );
}



// =============================================================================
// v2.3.1 — COMMERCES TAB (CRUD admin)
// =============================================================================
const COMMERCE_CATS = ["restaurant", "boutique", "pharmacie", "service", "autre"];

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function CommercesTab({ token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalDeleted, setTotalDeleted] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [q, setQ] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [communes, setCommunes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const qDeb = useDebounced(q, 300);

  useEffect(() => {
    axios.get(`${API}/v2/addressing/communes`).then((r) => setCommunes(r.data?.communes || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (qDeb) params.q = qDeb;
      if (communeFilter) params.commune = communeFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (includeDeleted) params.include_deleted = "true";
      const r = await axios.get(`${API}/ops/commerces`, { headers: authHeaders(token), params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setTotalDeleted(r.data?.total_deleted || 0);
    } catch (e) { toast.error("Erreur listing commerces"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, qDeb, communeFilter, categoryFilter, includeDeleted]); // eslint-disable-line

  const onDelete = async (id, nom) => {
    if (!window.confirm(`Soft delete "${nom}" ?\n(L'entrée est conservée 30 jours pour restauration.)`)) return;
    try {
      await axios.delete(`${API}/ops/commerces/${encodeURIComponent(id)}`, { headers: authHeaders(token) });
      toast.success("Commerce supprimé (soft)");
      load();
    } catch (e) { toast.error(e.response?.data?.error || "Erreur suppression"); }
  };
  const onRestore = async (id) => {
    try {
      await axios.post(`${API}/ops/commerces/${encodeURIComponent(id)}/restore`, {}, { headers: authHeaders(token) });
      toast.success("Commerce restauré");
      load();
    } catch (e) { toast.error("Erreur restauration"); }
  };

  return (
    <div data-testid="commerces-tab">
      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>🏪 Commerces · <span style={{ color: "#94a3b8", fontSize: 14, fontWeight: 400 }}>{total} actifs / {totalDeleted} supprimés</span></h2>
        <input
          type="text"
          placeholder="🔍 nom / adresse virtuelle / contact"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #334155", background: "#1e293b", color: "#f1f5f9", minWidth: 280 }}
          data-testid="commerces-search"
        />
        <select value={communeFilter} onChange={(e) => { setCommuneFilter(e.target.value); setPage(1); }} style={selectStyleAdmin} data-testid="commerces-commune-filter">
          <option value="">Toutes communes</option>
          {communes.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} style={selectStyleAdmin} data-testid="commerces-cat-filter">
          <option value="">Toutes catégories</option>
          {COMMERCE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#cbd5e1", fontSize: 13 }} data-testid="commerces-include-deleted-wrap">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1); }} data-testid="commerces-include-deleted" />
          Inclure supprimés
        </label>
        <button onClick={load} style={btnSecondaryAdmin} data-testid="commerces-refresh">🔄</button>
        <button onClick={() => {
          const params = new URLSearchParams();
          if (qDeb) params.set('q', qDeb);
          if (communeFilter) params.set('commune', communeFilter);
          if (categoryFilter) params.set('category', categoryFilter);
          if (includeDeleted) params.set('include_deleted', 'true');
          const url = `${API}/ops/commerces/export.csv?${params.toString()}`;
          // Fetch avec token + blob download.
          fetch(url, { headers: authHeaders(token) })
            .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob().then((b) => ({ b, name: (r.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/i)?.[1] || 'commerces.csv' })); })
            .then(({ b, name }) => {
              const a = document.createElement('a');
              a.href = URL.createObjectURL(b);
              a.download = name;
              document.body.appendChild(a);
              a.click();
              setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
              toast.success('Export CSV téléchargé');
            })
            .catch((e) => toast.error('Erreur export: ' + e.message));
        }} style={{ ...btnSecondaryAdmin, background: '#16a34a', color: '#fff', borderColor: '#16a34a' }} data-testid="commerces-export-csv">📥 Export CSV</button>
      </div>
      <table style={tableStyleAdmin} data-testid="commerces-table">
        <thead>
          <tr>
            <th>ID</th><th>Nom</th><th>Catégorie</th><th>Commune</th><th>Adresse virtuelle</th><th>Contact</th><th>Status</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8} style={{ textAlign: "center", padding: 16 }}>Chargement…</td></tr>}
          {!loading && items.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: 16, color: "#94a3b8" }}>Aucun commerce</td></tr>}
          {items.map((f) => {
            const p = f.properties || {};
            const deleted = !!p.deleted_at;
            return (
              <tr key={p.id_etablissement} style={deleted ? { opacity: 0.5, fontStyle: "italic" } : {}} data-testid={`commerce-row-${p.id_etablissement}`}>
                <td><code style={{ fontSize: 11 }}>{p.id_etablissement}</code></td>
                <td>{p.nom}</td>
                <td>{p.categorie}</td>
                <td>{p.commune}</td>
                <td><code style={{ fontSize: 11 }}>{p.adresse_virtuelle}</code></td>
                <td>{p.contact || "—"}</td>
                <td>{deleted ? <span style={{ color: "#f87171", fontSize: 12 }}>supprimé {p.deleted_at?.slice(0, 10)}</span> : <span style={{ color: "#4ade80", fontSize: 12 }}>● actif</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!deleted && <button onClick={() => setEditing(f)} style={btnIconAdmin} title="Éditer" data-testid={`commerce-edit-${p.id_etablissement}`}>✏️</button>}
                  {!deleted && <button onClick={() => onDelete(p.id_etablissement, p.nom)} style={{ ...btnIconAdmin, color: "#f87171" }} title="Supprimer" data-testid={`commerce-delete-${p.id_etablissement}`}>🗑</button>}
                  {deleted && <button onClick={() => onRestore(p.id_etablissement)} style={{ ...btnIconAdmin, color: "#4ade80" }} title="Restaurer" data-testid={`commerce-restore-${p.id_etablissement}`}>↩️</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btnSecondaryAdmin} data-testid="commerces-prev">‹ Précédent</button>
        <span style={{ color: "#94a3b8", fontSize: 13 }}>Page {page} · {Math.ceil(total / pageSize) || 1}</span>
        <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} style={btnSecondaryAdmin} data-testid="commerces-next">Suivant ›</button>
      </div>
      {editing && (
        <CommerceEditModal
          feature={editing}
          communes={communes}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CommerceEditModal({ feature, communes, token, onClose, onSaved }) {
  const p = feature.properties || {};
  const [lng, latInit] = feature.geometry?.coordinates || [];
  const [draft, setDraft] = useState({
    nom: p.nom || "",
    categorie: p.categorie || "autre",
    contact: p.contact || "",
    description_nouchi: p.description_nouchi || "",
    commune: p.commune || "",
    street_id: p.street_id || "",
    lat: latInit,
    lng: lng,
  });
  const [busy, setBusy] = useState(false);
  const streets = communes.find((c) => c.name === draft.commune)?.streets || [];

  const submit = async () => {
    if (!draft.nom.trim()) { toast.error("Nom requis"); return; }
    if (typeof draft.lat !== "number" || draft.lat < -90 || draft.lat > 90) { toast.error("Latitude invalide (-90..90)"); return; }
    if (typeof draft.lng !== "number" || draft.lng < -180 || draft.lng > 180) { toast.error("Longitude invalide (-180..180)"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/ops/commerces/${encodeURIComponent(p.id_etablissement)}`, {
        properties: { nom: draft.nom.trim(), categorie: draft.categorie, contact: draft.contact, description_nouchi: draft.description_nouchi, commune: draft.commune, street_id: draft.street_id },
        geometry: { coordinates: [draft.lng, draft.lat] },
      }, { headers: authHeaders(token) });
      toast.success("Commerce mis à jour (adresse recalculée si coords/rue changées)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={modalBackdropAdmin} data-testid="commerce-edit-modal">
      <div onClick={(e) => e.stopPropagation()} style={modalBoxAdmin}>
        <h3 style={{ margin: "0 0 4px" }}>✏️ Éditer commerce</h3>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#94a3b8" }}>ID : <code>{p.id_etablissement}</code> · Adresse : <code>{p.adresse_virtuelle}</code></p>
        <FieldV231 label="Nom"><input value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} style={inputStyleAdmin} data-testid="commerce-edit-nom" /></FieldV231>
        <FieldV231 label="Catégorie"><select value={draft.categorie} onChange={(e) => setDraft({ ...draft, categorie: e.target.value })} style={inputStyleAdmin} data-testid="commerce-edit-cat">{COMMERCE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select></FieldV231>
        <FieldV231 label="Contact"><input value={draft.contact} onChange={(e) => setDraft({ ...draft, contact: e.target.value })} style={inputStyleAdmin} data-testid="commerce-edit-contact" /></FieldV231>
        <FieldV231 label="Description (Nouchi)"><textarea rows={2} value={draft.description_nouchi} onChange={(e) => setDraft({ ...draft, description_nouchi: e.target.value })} style={inputStyleAdmin} data-testid="commerce-edit-desc" /></FieldV231>
        <FieldV231 label="Commune"><select value={draft.commune} onChange={(e) => setDraft({ ...draft, commune: e.target.value, street_id: "" })} style={inputStyleAdmin} data-testid="commerce-edit-commune"><option value="">—</option>{communes.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}</select></FieldV231>
        <FieldV231 label="Rue (street_id)"><select value={draft.street_id} onChange={(e) => setDraft({ ...draft, street_id: e.target.value })} style={inputStyleAdmin} data-testid="commerce-edit-street"><option value="">—</option>{streets.map((s) => <option key={s.street_id} value={s.street_id}>{s.street_id} — {s.nom}</option>)}</select></FieldV231>
        <div style={{ display: "flex", gap: 8 }}>
          <FieldV231 label="Latitude"><input type="number" step="0.0001" value={draft.lat} onChange={(e) => setDraft({ ...draft, lat: parseFloat(e.target.value) })} style={inputStyleAdmin} data-testid="commerce-edit-lat" /></FieldV231>
          <FieldV231 label="Longitude"><input type="number" step="0.0001" value={draft.lng} onChange={(e) => setDraft({ ...draft, lng: parseFloat(e.target.value) })} style={inputStyleAdmin} data-testid="commerce-edit-lng" /></FieldV231>
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }} data-testid="commerce-edit-map-wrap">
          <MapPreview lat={draft.lat} lng={draft.lng} />
        </div>
        <p style={{ fontSize: 11, color: "#fbbf24", marginTop: 8 }}>💡 Si coords ou street_id changent, l'adresse virtuelle est recalculée automatiquement.</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={onClose} style={btnSecondaryAdmin} data-testid="commerce-edit-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} style={btnPrimaryAdmin} data-testid="commerce-edit-save">{busy ? "…" : "Sauvegarder"}</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// v2.3.1 — ADDRESSES TAB (CRUD admin sur le registry)
// =============================================================================
function AddressesTab({ token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalDeleted, setTotalDeleted] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [q, setQ] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [communes, setCommunes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const qDeb = useDebounced(q, 300);

  useEffect(() => {
    axios.get(`${API}/v2/addressing/communes`).then((r) => setCommunes(r.data?.communes || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (qDeb) params.q = qDeb;
      if (communeFilter) params.commune = communeFilter;
      if (includeDeleted) params.include_deleted = "true";
      const r = await axios.get(`${API}/ops/addresses`, { headers: authHeaders(token), params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setTotalDeleted(r.data?.total_deleted || 0);
    } catch (e) { toast.error("Erreur listing addresses"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, qDeb, communeFilter, includeDeleted]); // eslint-disable-line

  const onDelete = async (code) => {
    if (!window.confirm(`Soft delete ${code} ?`)) return;
    try {
      await axios.delete(`${API}/ops/addresses/${encodeURIComponent(code)}`, { headers: authHeaders(token) });
      toast.success("Adresse supprimée");
      load();
    } catch (e) { toast.error("Erreur"); }
  };
  const onRestore = async (code) => {
    try {
      await axios.post(`${API}/ops/addresses/${encodeURIComponent(code)}/restore`, {}, { headers: authHeaders(token) });
      toast.success("Adresse restaurée");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="addresses-tab">
      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>📍 Registry d'adresses · <span style={{ color: "#94a3b8", fontSize: 14, fontWeight: 400 }}>{total} actifs / {totalDeleted} supprimés</span></h2>
        <input type="text" placeholder="🔍 code / commune / rue" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #334155", background: "#1e293b", color: "#f1f5f9", minWidth: 280 }} data-testid="addresses-search" />
        <select value={communeFilter} onChange={(e) => { setCommuneFilter(e.target.value); setPage(1); }} style={selectStyleAdmin} data-testid="addresses-commune-filter">
          <option value="">Toutes communes</option>
          {communes.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#cbd5e1", fontSize: 13 }}>
          <input type="checkbox" checked={includeDeleted} onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1); }} data-testid="addresses-include-deleted" />
          Inclure supprimés
        </label>
        <button onClick={load} style={btnSecondaryAdmin} data-testid="addresses-refresh">🔄</button>
      </div>
      <table style={tableStyleAdmin} data-testid="addresses-table">
        <thead>
          <tr><th>Code</th><th>Commune</th><th>Rue</th><th>Distance</th><th>Lat</th><th>Lng</th><th>Source</th><th>Status</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={9} style={{ textAlign: "center", padding: 16 }}>Chargement…</td></tr>}
          {!loading && items.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 16, color: "#94a3b8" }}>Aucune adresse</td></tr>}
          {items.map((a) => {
            const deleted = !!a.deleted_at;
            return (
              <tr key={a.code} style={deleted ? { opacity: 0.5, fontStyle: "italic" } : {}} data-testid={`address-row-${a.code}`}>
                <td><code style={{ fontSize: 11 }}>{a.code}</code></td>
                <td>{a.commune || "—"}</td>
                <td>{a.street_name || "—"}</td>
                <td>{a.distance_m != null ? `${a.distance_m}m` : "—"}</td>
                <td>{a.lat?.toFixed(4)}</td>
                <td>{a.lng?.toFixed(4)}</td>
                <td><span style={{ fontSize: 11, color: "#94a3b8" }}>{a.source}</span></td>
                <td>{deleted ? <span style={{ color: "#f87171", fontSize: 12 }}>supprimé {a.deleted_at?.slice(0, 10)}</span> : <span style={{ color: "#4ade80", fontSize: 12 }}>● actif</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!deleted && <button onClick={() => setEditing(a)} style={btnIconAdmin} title="Éditer" data-testid={`address-edit-${a.code}`}>✏️</button>}
                  {!deleted && <button onClick={() => onDelete(a.code)} style={{ ...btnIconAdmin, color: "#f87171" }} title="Supprimer" data-testid={`address-delete-${a.code}`}>🗑</button>}
                  {deleted && <button onClick={() => onRestore(a.code)} style={{ ...btnIconAdmin, color: "#4ade80" }} title="Restaurer" data-testid={`address-restore-${a.code}`}>↩️</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btnSecondaryAdmin} data-testid="addresses-prev">‹ Précédent</button>
        <span style={{ color: "#94a3b8", fontSize: 13 }}>Page {page} · {Math.ceil(total / pageSize) || 1}</span>
        <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} style={btnSecondaryAdmin} data-testid="addresses-next">Suivant ›</button>
      </div>
      {editing && (
        <AddressEditModal
          entry={editing}
          communes={communes}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AddressEditModal({ entry, communes, token, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    commune: entry.commune || "",
    street_id: entry.street_id || "",
    street_name: entry.street_name || "",
    lat: entry.lat,
    lng: entry.lng,
  });
  const [busy, setBusy] = useState(false);
  const streets = communes.find((c) => c.name === draft.commune)?.streets || [];
  const submit = async () => {
    if (typeof draft.lat !== "number" || draft.lat < -90 || draft.lat > 90) { toast.error("Latitude invalide"); return; }
    if (typeof draft.lng !== "number" || draft.lng < -180 || draft.lng > 180) { toast.error("Longitude invalide"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/ops/addresses/${encodeURIComponent(entry.code)}`, draft, { headers: authHeaders(token) });
      toast.success("Adresse mise à jour (distance recalculée si lat/lng modifiés)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={modalBackdropAdmin} data-testid="address-edit-modal">
      <div onClick={(e) => e.stopPropagation()} style={modalBoxAdmin}>
        <h3 style={{ margin: "0 0 4px" }}>✏️ Éditer adresse</h3>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#94a3b8" }}>Code (immuable) : <code>{entry.code}</code></p>
        <FieldV231 label="Commune"><select value={draft.commune} onChange={(e) => setDraft({ ...draft, commune: e.target.value, street_id: "" })} style={inputStyleAdmin} data-testid="address-edit-commune"><option value="">—</option>{communes.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}</select></FieldV231>
        <FieldV231 label="Rue (street_id)"><select value={draft.street_id} onChange={(e) => setDraft({ ...draft, street_id: e.target.value, street_name: streets.find(s => s.street_id === e.target.value)?.nom || draft.street_name })} style={inputStyleAdmin} data-testid="address-edit-street"><option value="">—</option>{streets.map((s) => <option key={s.street_id} value={s.street_id}>{s.street_id} — {s.nom}</option>)}</select></FieldV231>
        <FieldV231 label="Nom de rue"><input value={draft.street_name} onChange={(e) => setDraft({ ...draft, street_name: e.target.value })} style={inputStyleAdmin} data-testid="address-edit-street-name" /></FieldV231>
        <div style={{ display: "flex", gap: 8 }}>
          <FieldV231 label="Latitude"><input type="number" step="0.0001" value={draft.lat} onChange={(e) => setDraft({ ...draft, lat: parseFloat(e.target.value) })} style={inputStyleAdmin} data-testid="address-edit-lat" /></FieldV231>
          <FieldV231 label="Longitude"><input type="number" step="0.0001" value={draft.lng} onChange={(e) => setDraft({ ...draft, lng: parseFloat(e.target.value) })} style={inputStyleAdmin} data-testid="address-edit-lng" /></FieldV231>
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }} data-testid="address-edit-map-wrap">
          <MapPreview lat={draft.lat} lng={draft.lng} />
        </div>
        <p style={{ fontSize: 11, color: "#fbbf24", marginTop: 8 }}>💡 Le code est immuable. distance_m est recalculée à partir de lat/lng et du start_lat/lon de la rue.</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={onClose} style={btnSecondaryAdmin} data-testid="address-edit-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} style={btnPrimaryAdmin} data-testid="address-edit-save">{busy ? "…" : "Sauvegarder"}</button>
        </div>
      </div>
    </div>
  );
}

// Helpers visuels mutualisés (v2.3.1).
function FieldV231({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 8, fontSize: 12, color: "#cbd5e1", flex: 1 }}>
      <span style={{ display: "block", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
const inputStyleAdmin = { width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #334155", background: "#0f172a", color: "#f1f5f9", fontSize: 13, boxSizing: "border-box" };
const selectStyleAdmin = { padding: "7px 10px", borderRadius: 6, border: "1px solid #334155", background: "#1e293b", color: "#f1f5f9", fontSize: 13 };
const tableStyleAdmin = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const btnIconAdmin = { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, padding: "4px 6px", color: "#cbd5e1" };
const btnPrimaryAdmin = { padding: "8px 14px", background: "#fbbf24", color: "#0f172a", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" };
const btnSecondaryAdmin = { padding: "7px 12px", background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155", borderRadius: 6, cursor: "pointer", fontSize: 13 };
const modalBackdropAdmin = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBoxAdmin = { background: "#0f172a", color: "#f1f5f9", border: "1px solid #334155", borderRadius: 12, padding: 22, width: "min(520px, 92vw)", maxHeight: "92vh", overflowY: "auto" };


// v2.3.3 — MapPreview : mini-Leaflet 240×160 live update sur change lat/lng (debounced 300ms).
if (typeof L?.Icon?.Default?.mergeOptions === "function") {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
}
function MapRecenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng)) {
      map.flyTo([lat, lng], 16, { duration: 0.6 });
    }
  }, [lat, lng, map]);
  return null;
}
function MapPreview({ lat, lng }) {
  const [debLat, setDebLat] = useState(lat);
  const [debLng, setDebLng] = useState(lng);
  useEffect(() => {
    const t = setTimeout(() => { setDebLat(lat); setDebLng(lng); }, 300);
    return () => clearTimeout(t);
  }, [lat, lng]);
  const valid = typeof debLat === "number" && typeof debLng === "number" && Number.isFinite(debLat) && Number.isFinite(debLng) && debLat >= -90 && debLat <= 90 && debLng >= -180 && debLng <= 180;
  if (!valid) {
    return (
      <div style={{ width: 240, height: 160, background: "#1e293b", border: "1px solid #334155", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 12 }} data-testid="map-preview-invalid">
        📍 Coordonnées invalides
      </div>
    );
  }
  return (
    <div style={{ width: 240, height: 160, borderRadius: 8, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }} data-testid="map-preview">
      <MapContainer center={[debLat, debLng]} zoom={16} scrollWheelZoom={false} dragging={false} attributionControl={false} style={{ width: "100%", height: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
        <Marker position={[debLat, debLng]} />
        <MapRecenter lat={debLat} lng={debLng} />
      </MapContainer>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PURGE TAB — v2.3.2 : statut du cron + déclenchement manuel
// ---------------------------------------------------------------------------
function PurgeTab({ token }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ops/purge/status`, { headers: authHeaders(token) });
      setState(r.data);
    } catch (e) {
      toast.error("Impossible de charger le statut purge");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token]); // eslint-disable-line

  const runNow = async () => {
    if (!window.confirm("Lancer une purge manuelle maintenant ?\n(Supprime définitivement les items soft-deleted depuis plus de 30 jours)")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ops/purge/run`, {}, { headers: authHeaders(token) });
      setLastResult(r.data);
      setState(r.data.state);
      const { commerces_purged = 0, addresses_purged = 0, skipped } = r.data;
      if (skipped) toast("Purge déjà en cours, requête ignorée.");
      else toast.success(`Purge OK — commerces: ${commerces_purged}, adresses: ${addresses_purged}`);
    } catch (e) {
      toast.error("Erreur lors de la purge");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="ops-loading">Chargement…</div>;

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR") : "—";

  return (
    <div style={{ padding: 16, maxWidth: 720 }} data-testid="purge-tab">
      <h2 style={{ marginTop: 0 }}>🧹 Purge automatique (cron)</h2>
      <p style={{ color: "#555", fontSize: 14, marginTop: 4 }}>
        Le cron s'exécute toutes les 6h et purge physiquement les enregistrements
        soft-deleted depuis plus de <strong>{state.retention_days} jours</strong>.
        Les opérations OPS quotidiennes restent en soft-delete uniquement.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <Card label="Dernière exécution" value={fmt(state.last_run_at)} />
        <Card label="Prochaine exécution" value={fmt(state.next_run_at)} />
        <Card label="Rétention (jours)" value={state.retention_days} />
        <Card label="Total purgé (cumulé)" value={state.total_purged_lifetime} />
      </div>

      <div style={{ marginTop: 20, display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="button"
          onClick={runNow}
          disabled={busy || state.running}
          data-testid="purge-run-now"
          style={{
            background: "#dc2626", color: "#fff", border: "none",
            padding: "10px 16px", borderRadius: 6, cursor: busy ? "wait" : "pointer",
            opacity: (busy || state.running) ? 0.6 : 1, fontWeight: 600,
          }}
        >
          {busy ? "Purge en cours…" : "🗑️ Lancer une purge maintenant"}
        </button>
        <button
          type="button"
          onClick={load}
          className="ops-btn ops-btn-ghost"
          data-testid="purge-refresh"
        >
          🔄 Refresh
        </button>
        {state.running && <span style={{ color: "#dc2626", fontSize: 13 }}>● Purge active…</span>}
      </div>

      {lastResult && (
        <div style={{ marginTop: 16, background: "#f9fafb", padding: 12, borderRadius: 6, fontSize: 13 }} data-testid="purge-last-result">
          <strong>Dernière exécution manuelle :</strong>{" "}
          commerces purgés <code>{lastResult.commerces_purged}</code> ·{" "}
          adresses purgées <code>{lastResult.addresses_purged}</code>
          {lastResult.skipped && <span style={{ color: "#dc2626" }}> (skipped — lock actif)</span>}
        </div>
      )}
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: "#111" }}>{value}</div>
    </div>
  );
}
